import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import { authFetch } from '@/lib/authFetch';
import DocumentViewer from '@/components/DocumentViewer';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/* ─── PDF Viewer (client-only) ─── */
const PdfViewer = dynamic(() => import('@/components/PdfViewer'), {
  ssr: false,
  loading: () => (
    <div className="h-full flex items-center justify-center bg-slate-100">
      <div className="text-center space-y-3">
        <span className="material-symbols-outlined text-[48px] text-calm-accent animate-pulse">picture_as_pdf</span>
        <p className="text-sm font-light text-calm-muted">Cargando visor PDF…</p>
      </div>
    </div>
  ),
});

/* ─── Icon helper ─── */
const Icon = ({ name, className }: { name: string; className?: string }) => (
  <span className={cn('material-symbols-outlined', className)}>{name}</span>
);

interface Cliente {
  id: string;
  razon_social: string;
  nombre_comercial: string;
  nif: string;
}

interface DocumentoPendiente {
  id: string;
  nombre: string;
  tipo: string;
  fecha_subida: string;
  tamanio: number;
  url_archivo: string;
  datos_ia?: any;
  cliente_id: string;
  estado?: string;
}

interface DocumentoConCliente extends DocumentoPendiente {
  cliente: Cliente;
}

interface ClienteConDocumentos {
  cliente: Cliente;
  documentos: DocumentoPendiente[];
  documentosProcesados: number;
  ultimoDocumentoFecha?: Date;
}

interface LineaIVAForm {
  tipo: number;
  base: number;
  cuota: number;
  cuenta?: string;
}

interface LineaDetalle {
  descripcion: string;
  cantidad?: number;
  precio_unitario?: number;
  importe_linea?: number;
  es_suplido?: boolean;
  // Legacy/alternative field names
  honorarios?: number;
  total?: number;
  bases?: string;
  arancel?: string;
}

interface SuplidoItem {
  concepto: string;
  importe: number;
  nif_tercero?: string;
}

interface FinancialData {
  fechaFactura?: string;
  numeroFactura?: string;
  nifEmisor?: string;
  razonSocialEmisor?: string;
  direccionEmisor?: string;
  nifReceptor?: string;
  razonSocialReceptor?: string;
  direccionReceptor?: string;
  baseImponible?: number;
  tipoIVA?: number;
  cuotaIVA?: number;
  totalFactura?: number;
  tipoDocumento?: string;
  actividad?: string;
  fechaContable?: string;
  fechaVencimiento?: string;
  porcentajeRetencion?: number;
  importeRetencion?: number;
  tipoRetencion?: string;
  lineasIVA?: LineaIVAForm[];
  suplidos?: number;
  suplidosDetalle?: SuplidoItem[];
  lineasDetalle?: LineaDetalle[];
  totalSuplidos?: number;
  totalBruto?: number;
  totalNeto?: number;
  erpTotal?: number;
  cuentaProveedor?: string;
  cuentasGasto?: string[];
  metodoPago?: string;
  nivelConfianza?: number;
  tipoOperacion?: string;
}

interface IVABreakdown {
  baseIVA21?: number;
  iva21?: number;
  baseIVA10?: number;
  iva10?: number;
  baseIVA4?: number;
  iva4?: number;
  baseIVA0?: number;
}

const ESTADO_INFO: Record<string, { label: string; dot: string }> = {
  procesado_ia:       { label: 'En proceso',       dot: 'bg-blue-400' },
  error:              { label: 'Error',             dot: 'bg-red-400' },
  pendiente:          { label: 'Pend. revisión',    dot: 'bg-amber-400' },
  contabilizando:     { label: 'Contabilizando',    dot: 'bg-sky-400' },
  contabilizado:      { label: 'Contabilizado',     dot: 'bg-emerald-400' },
  validado:           { label: 'Validado',          dot: 'bg-teal-400' },
};

const STATUS_PILLS: Array<{ key: string; label: string; matchEstados: string[] }> = [
  { key: 'todos',              label: 'Todos',             matchEstados: [] },
  { key: 'en_proceso',         label: 'En proceso',        matchEstados: ['procesado_ia'] },
  { key: 'errores',            label: 'Errores',           matchEstados: ['error'] },
  { key: 'pendiente_revision', label: 'Pend. de revisión', matchEstados: ['pendiente'] },
  { key: 'contabilizando',     label: 'Contabilizando',    matchEstados: ['contabilizando'] },
  { key: 'contabilizados',     label: 'Contabilizados',    matchEstados: ['contabilizado', 'validado'] },
];

const getEstadoInfo = (estado: string) =>
  ESTADO_INFO[estado] || { label: estado || 'Desconocido', dot: 'bg-slate-300' };

export default function DocumentosPendientes() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [clientesConDocs, setClientesConDocs] = useState<ClienteConDocumentos[]>([]);
  const [allClientes, setAllClientes] = useState<Cliente[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [mostrarVisor, setMostrarVisor] = useState(false);
  const [documentoActual, setDocumentoActual] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [mostrarSubida, setMostrarSubida] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Upload form controlled state
  const [uploadClienteId, setUploadClienteId] = useState('');
  const [uploadTipo, setUploadTipo] = useState('');
  const [uploadNotas, setUploadNotas] = useState('');
  
  // Financial data states
  const [editingFinancial, setEditingFinancial] = useState(false);
  const [financialData, setFinancialData] = useState<FinancialData>({});
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [verifyingAI, setVerifyingAI] = useState(false);
  const [savingFinancial, setSavingFinancial] = useState(false);

  // Status filter and table view states
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [allDocumentos, setAllDocumentos] = useState<DocumentoConCliente[]>([]);
  const [fechaContable, setFechaContable] = useState<string>('fecha_factura');
  const [separarPaginas, setSepararPaginas] = useState(false);
  const [fechaContableCustom, setFechaContableCustom] = useState<string>('');

  // Split-view state
  const [mostrarSplitView, setMostrarSplitView] = useState(false);
  const [documentosPendientesList, setDocumentosPendientesList] = useState<DocumentoConCliente[]>([]);
  const [indiceDocumentoActual, setIndiceDocumentoActual] = useState(0);
  const [splitFormSections, setSplitFormSections] = useState({ datos: true, financieros: true });

  // Acciones Masivas modal state
  const [mostrarCambiosMasivos, setMostrarCambiosMasivos] = useState(false);
  const [cambiosMasivosForm, setCambiosMasivosForm] = useState({
    fechaFactura: '',
    fechaVencimiento: '',
    fechaContable: '',
    actividad: '',
    serie: '',
    posicionFiscal: '',
    bienesInversion: false,
    deducibilidad: '',
  });

  // Punteo state
  const [punteados, setPunteados] = useState<Set<string>>(new Set());

  // Bulk action modal states
  const [mostrarCambiarEmpresa, setMostrarCambiarEmpresa] = useState(false);
  const [empresaDestinoId, setEmpresaDestinoId] = useState('');
  const [mostrarAsignarTercero, setMostrarAsignarTercero] = useState(false);
  const [terceroNif, setTerceroNif] = useState('');
  const [terceroNombre, setTerceroNombre] = useState('');
  const [procesandoAccion, setProcesandoAccion] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadPendingDocuments = useCallback(async () => {
    try {
      setLoadingData(true);
      
      if (!user?.id) {
        console.error('No user ID found');
        toast.error('Error: No se pudo obtener el ID del usuario');
        return;
      }

      
      // Get advisor's assigned clients
      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(*)')
        .eq('asesor_id', user.id);

      if (clientError) {
        console.error('Error loading assigned clients:', clientError);
        throw clientError;
      }


      // Store all assigned clients for the upload modal
      const todosClientes: Cliente[] = [];
      assignedClients?.forEach(ac => {
        if (ac.clientes) {
          todosClientes.push(ac.clientes as unknown as Cliente);
        }
      });
      setAllClientes(todosClientes);

      const clientIds = assignedClients?.map(ac => ac.cliente_id) || [];

      if (clientIds.length === 0) {
        console.log('No assigned clients found');
        setClientesConDocs([]);
        setAllDocumentos([]);
        return;
      }

      // Get pending documents
      const { data: pendingDocs, error: docsError } = await supabase
        .from('documentos')
        .select('*')
        .in('cliente_id', clientIds)
        .in('estado', ['pendiente', 'procesado_ia']);

      if (docsError) {
        console.error('Error loading documents:', docsError);
        throw docsError;
      }

      // Get processed documents count for each client
      const { data: processedDocs, error: processedError } = await supabase
        .from('documentos')
        .select('id, cliente_id, fecha_subida')
        .in('cliente_id', clientIds)
        .in('estado', ['contabilizado', 'validado']);

      if (processedError) {
        console.error('Error loading processed documents:', processedError);
      }

      // Get all documents to find last document date
      const { data: allDocs, error: allDocsError } = await supabase
        .from('documentos')
        .select('id, cliente_id, fecha_subida')
        .in('cliente_id', clientIds)
        .order('fecha_subida', { ascending: false });

      if (allDocsError) {
        console.error('Error loading all documents:', allDocsError);
      }

      // Group documents by client
      const grouped: ClienteConDocumentos[] = [];
      
      assignedClients?.forEach(ac => {
        const clienteDocs = pendingDocs?.filter(d => d.cliente_id === ac.cliente_id) || [];
        const processedCount = processedDocs?.filter(d => d.cliente_id === ac.cliente_id).length || 0;
        
        const clientAllDocs = allDocs?.filter(d => d.cliente_id === ac.cliente_id) || [];
        const ultimoDoc = clientAllDocs.length > 0 ? clientAllDocs[0] : null;
        const ultimoDocumentoFecha = ultimoDoc ? new Date(ultimoDoc.fecha_subida) : undefined;

        if (ac.clientes) {
          grouped.push({
            cliente: ac.clientes as any,
            documentos: clienteDocs as any,
            documentosProcesados: processedCount,
            ultimoDocumentoFecha
          });
        }
      });

      setClientesConDocs(grouped);

      // Build full document list for table view
      const clienteMap = new Map<string, Cliente>();
      todosClientes.forEach(c => clienteMap.set(c.id, c));

      const { data: fullTableDocs, error: fullTableDocsError } = await supabase
        .from('documentos')
        .select('*')
        .in('cliente_id', clientIds)
        .order('fecha_subida', { ascending: false });

      if (fullTableDocsError) {
        console.error('Error loading full documents for table:', fullTableDocsError);
      }

      const mappedTableDocs: DocumentoConCliente[] = (fullTableDocs || []).map((doc) => {
        const d = doc as DocumentoPendiente;
        return {
          ...d,
          cliente: clienteMap.get(d.cliente_id) || { id: '', razon_social: 'Desconocido', nombre_comercial: '', nif: '' }
        };
      });
      setAllDocumentos(mappedTableDocs);

    } catch (error: any) {
      console.error('Error loading pending documents:', error);
      toast.error(`Error al cargar documentos pendientes: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadPendingDocuments();
    }
  }, [user, loading, router, loadPendingDocuments]);

  // Handle ?filter= URL param (from documentos-contabilizados redirect)
  useEffect(() => {
    const filterParam = router.query.filter as string | undefined;
    if (filterParam) {
      const pill = STATUS_PILLS.find(p => p.key === filterParam || p.matchEstados.includes(filterParam));
      if (pill) setStatusFilter(pill.key);
    }
  }, [router.query.filter]);

  const toggleDocSelection = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };


  const handleBulkDelete = async () => {
    if (selectedDocs.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    if (!confirm(`¿Eliminar ${selectedDocs.size} documento(s)?`)) return;

    try {
      const { error } = await supabase
        .from('documentos')
        .delete()
        .in('id', Array.from(selectedDocs));

      if (error) throw error;

      toast.success(`${selectedDocs.size} documento(s) eliminado(s)`);
      setSelectedDocs(new Set());
      loadPendingDocuments();
    } catch (error) {
      console.error('Error deleting documents:', error);
      toast.error('Error al eliminar documentos');
    }
  };

  const handleBulkDownload = () => {
    if (selectedDocs.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    const docsToDownload = allDocumentos.filter(d => selectedDocs.has(d.id) && d.url_archivo);
    if (docsToDownload.length === 0) {
      toast.error('Los documentos seleccionados no tienen archivos disponibles');
      return;
    }
    toast.success(`Descargando ${docsToDownload.length} documento(s)...`);
    docsToDownload.forEach((doc, index) => {
      setTimeout(() => {
        window.open(doc.url_archivo, '_blank');
      }, index * 500);
    });
  };

  const handlePostAutomatically = async () => {
    if (selectedDocs.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    try {
      const response = await fetch('/api/documents/post-automatically', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentIds: Array.from(selectedDocs) })
      });

      if (!response.ok) throw new Error('Error posting documents');

      const data = await response.json();
      toast.success(`${data.posted} documento(s) contabilizado(s) automaticamente`);
      setSelectedDocs(new Set());
      loadPendingDocuments();
    } catch (error) {
      console.error('Error posting documents:', error);
      toast.error('Error al contabilizar documentos');
    }
  };

  // Punteo handlers
  const togglePunteado = (id: string) => {
    setPunteados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkPuntear = () => {
    setPunteados(prev => {
      const next = new Set(prev);
      selectedDocs.forEach(id => next.add(id));
      return next;
    });
    toast.success(`${selectedDocs.size} documento(s) marcado(s) como punteado`);
  };

  const handleBulkDesPuntear = () => {
    setPunteados(prev => {
      const next = new Set(prev);
      selectedDocs.forEach(id => next.delete(id));
      return next;
    });
    toast.success(`${selectedDocs.size} documento(s) desmarcado(s)`);
  };

  // ═══════════════ BULK ACTION HANDLERS ═══════════════

  const handleMergeDocuments = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length < 2) { toast.error('Selecciona al menos 2 documentos para unir'); return; }
    const pdfDocs = allDocumentos.filter(d => ids.includes(d.id) && d.nombre.toLowerCase().endsWith('.pdf'));
    if (pdfDocs.length < 2) { toast.error('Se necesitan al menos 2 PDFs para unir'); return; }
    setProcesandoAccion(true);
    const t = toast.loading('Uniendo documentos PDF...');
    try {
      const res = await fetch('/api/documents/merge-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: pdfDocs.map(d => d.id) }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Error al unir');
      toast.success(`${data.documents_merged} documentos unidos (${data.pages_total} páginas)`, { id: t });
      setSelectedDocs(new Set());
      loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al unir documentos', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleSplitPages = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('Selecciona al menos un documento'); return; }
    const pdfDocs = allDocumentos.filter(d => ids.includes(d.id) && d.nombre.toLowerCase().endsWith('.pdf'));
    if (pdfDocs.length === 0) { toast.error('No hay PDFs seleccionados para separar'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Separando ${pdfDocs.length} documento(s)...`);
    let totalCreated = 0;
    try {
      for (const doc of pdfDocs) {
        const res = await fetch('/api/documents/split-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ document_id: doc.id }),
        });
        const data = await res.json();
        if (data.success) totalCreated += data.documents_created;
      }
      toast.success(`${totalCreated} documento(s) creado(s) a partir de la separación`, { id: t });
      setSelectedDocs(new Set());
      loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al separar páginas', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleCambiarEmpresaBulk = async () => {
    if (!empresaDestinoId) { toast.error('Selecciona una empresa destino'); return; }
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('No hay documentos seleccionados'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Cambiando empresa de ${ids.length} documento(s)...`);
    try {
      const { error } = await supabase
        .from('documentos')
        .update({ empresa_id: empresaDestinoId })
        .in('id', ids);
      if (error) throw error;
      toast.success(`${ids.length} documento(s) movido(s) a nueva empresa`, { id: t });
      setSelectedDocs(new Set());
      setMostrarCambiarEmpresa(false);
      setEmpresaDestinoId('');
      loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al cambiar empresa', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleAsignarTerceroBulk = async () => {
    if (!terceroNif.trim()) { toast.error('Ingresa el NIF del tercero'); return; }
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('No hay documentos seleccionados'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Asignando tercero a ${ids.length} documento(s)...`);
    try {
      const docsToUpdate = allDocumentos.filter(d => ids.includes(d.id));
      for (const doc of docsToUpdate) {
        const currentIA = doc.datos_ia || {};
        const updatedIA = {
          ...currentIA,
          nifEmisor: terceroNif.trim(),
          razonSocialEmisor: terceroNombre.trim() || currentIA.razonSocialEmisor || '',
          ...(currentIA.emisor ? { emisor: { ...currentIA.emisor, nif: terceroNif.trim(), nombre: terceroNombre.trim() || currentIA.emisor?.nombre || '' } } : {}),
        };
        await supabase.from('documentos').update({ datos_ia: updatedIA }).eq('id', doc.id);
      }
      toast.success(`Tercero asignado a ${ids.length} documento(s)`, { id: t });
      setSelectedDocs(new Set());
      setMostrarAsignarTercero(false);
      setTerceroNif('');
      setTerceroNombre('');
      loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al asignar tercero', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleRotateDocuments = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('Selecciona al menos un documento'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Rotando ${ids.length} documento(s)...`);
    try {
      const res = await fetch('/api/documents/rotate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: ids, angle: 90 }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Error al rotar');
      toast.success(`${data.rotated} de ${data.total} documento(s) rotado(s) 90°`, { id: t });
      loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al rotar documentos', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleCalcularBaseYCuota = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('Selecciona al menos un documento'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Calculando base y cuota de ${ids.length} documento(s)...`);
    let updated = 0;
    try {
      const docsToCalc = allDocumentos.filter(d => ids.includes(d.id) && d.datos_ia);
      for (const doc of docsToCalc) {
        const ai = doc.datos_ia;
        const total = ai.totalFactura ?? ai.totales?.total_factura;
        const ivaPercent = ai.tipoIVA ?? ai.lineas_impuesto?.[0]?.porcentaje ?? 21;
        if (total && ivaPercent) {
          const base = Number((total / (1 + ivaPercent / 100)).toFixed(2));
          const cuota = Number((total - base).toFixed(2));
          const updatedIA = {
            ...ai,
            baseImponible: base,
            cuotaIVA: cuota,
            tipoIVA: ivaPercent,
            ...(ai.totales ? { totales: { ...ai.totales, base_imponible: base, total_impuestos: cuota } } : {}),
          };
          const { error } = await supabase.from('documentos').update({ datos_ia: updatedIA }).eq('id', doc.id);
          if (!error) updated++;
        }
      }
      toast.success(`Base y cuota calculadas en ${updated} documento(s)`, { id: t });
      if (updated > 0) loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al calcular', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleRedondeo2Decimales = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) { toast.error('Selecciona al menos un documento'); return; }
    setProcesandoAccion(true);
    const t = toast.loading(`Redondeando valores de ${ids.length} documento(s)...`);
    let updated = 0;
    try {
      const docsToRound = allDocumentos.filter(d => ids.includes(d.id) && d.datos_ia);
      const roundNum = (v: any) => typeof v === 'number' ? Number(v.toFixed(2)) : v;
      for (const doc of docsToRound) {
        const ai = { ...doc.datos_ia };
        if (ai.baseImponible != null) ai.baseImponible = roundNum(ai.baseImponible);
        if (ai.cuotaIVA != null) ai.cuotaIVA = roundNum(ai.cuotaIVA);
        if (ai.totalFactura != null) ai.totalFactura = roundNum(ai.totalFactura);
        if (ai.importeRetencion != null) ai.importeRetencion = roundNum(ai.importeRetencion);
        if (ai.suplidos != null && typeof ai.suplidos === 'number') ai.suplidos = roundNum(ai.suplidos);
        if (Array.isArray(ai.lineasIVA)) {
          ai.lineasIVA = ai.lineasIVA.map((l: any) => ({ ...l, base: roundNum(l.base), cuota: roundNum(l.cuota) }));
        }
        if (ai.totales) {
          ai.totales = { ...ai.totales, base_imponible: roundNum(ai.totales.base_imponible), total_impuestos: roundNum(ai.totales.total_impuestos), total_retenciones: roundNum(ai.totales.total_retenciones), total_suplidos: roundNum(ai.totales.total_suplidos), total_factura: roundNum(ai.totales.total_factura) };
        }
        if (Array.isArray(ai.lineas_impuesto)) {
          ai.lineas_impuesto = ai.lineas_impuesto.map((l: any) => ({ ...l, base_imponible: roundNum(l.base_imponible), cuota: roundNum(l.cuota) }));
        }
        if (Array.isArray(ai.lineas_detalle)) {
          ai.lineas_detalle = ai.lineas_detalle.map((l: any) => ({ ...l, precio_unitario: roundNum(l.precio_unitario), importe_linea: roundNum(l.importe_linea) }));
        }
        const { error } = await supabase.from('documentos').update({ datos_ia: ai }).eq('id', doc.id);
        if (!error) updated++;
      }
      toast.success(`Valores redondeados en ${updated} documento(s)`, { id: t });
      if (updated > 0) loadPendingDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Error al redondear', { id: t });
    } finally {
      setProcesandoAccion(false);
    }
  };

  // ZIP download
  const handleDownloadZip = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) return;
    toast.loading('Preparando ZIP...');
    try {
      const res = await fetch('/api/documents/download-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentoIds: ids }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al descargar ZIP');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documentos-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('ZIP descargado');
    } catch (error: unknown) {
      toast.dismiss();
      const msg = error instanceof Error ? error.message : 'Error al descargar ZIP';
      toast.error(msg);
    }
  };

  // Sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedDocumentos = (docs: DocumentoConCliente[]): DocumentoConCliente[] => {
    if (!sortColumn) return docs;
    return [...docs].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      switch (sortColumn) {
        case 'documento':
          aValue = a.nombre.toLowerCase();
          bValue = b.nombre.toLowerCase();
          break;
        case 'cliente':
          aValue = (a.cliente?.razon_social || '').toLowerCase();
          bValue = (b.cliente?.razon_social || '').toLowerCase();
          break;
        case 'estado':
          aValue = getEstadoInfo(a.estado || '').label.toLowerCase();
          bValue = getEstadoInfo(b.estado || '').label.toLowerCase();
          break;
        case 'asiento':
          aValue = ((a as any).nro_asiento_erp || '').toLowerCase();
          bValue = ((b as any).nro_asiento_erp || '').toLowerCase();
          break;
        case 'punteado':
          aValue = punteados.has(a.id) ? 1 : 0;
          bValue = punteados.has(b.id) ? 1 : 0;
          break;
        case 'fecha':
          aValue = new Date(a.fecha_subida).getTime();
          bValue = new Date(b.fecha_subida).getTime();
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortArrow = ({ col }: { col: string }) =>
    sortColumn === col ? <span className="ml-1 text-calm-muted">{sortDirection === 'asc' ? '↑' : '↓'}</span> : null;

  const handlePostManually = () => {
    if (selectedDocs.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    router.push(`/asesor/contabilizacion-manual?docs=${Array.from(selectedDocs).join(',')}`);
  };

  // Single document handlers
  const handleDownloadDocument = (docId: string) => {
    const doc = allDocumentos.find(d => d.id === docId);
    if (!doc || !doc.url_archivo) {
      toast.error('No se encontró el archivo del documento');
      return;
    }
    window.open(doc.url_archivo, '_blank');
    toast.success('Abriendo documento...');
  };

  const handlePostAutomaticallySingle = async (docId: string) => {
    try {
      const response = await fetch('/api/documents/post-automatically', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentIds: [docId] })
      });

      if (!response.ok) throw new Error('Error posting document');

      const data = await response.json();
      toast.success('Documento contabilizado automaticamente');
      loadPendingDocuments();
    } catch (error) {
      console.error('Error posting document:', error);
      toast.error('Error al contabilizar documento');
    }
  };

  const handlePostManuallySingle = (docId: string) => {
    router.push(`/asesor/contabilizacion-manual?docs=${docId}`);
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('¿Eliminar este documento?')) return;

    try {
      const { error } = await supabase
        .from('documentos')
        .delete()
        .eq('id', docId);

      if (error) throw error;

      toast.success('Documento eliminado');
      loadPendingDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error al eliminar documento');
    }
  };

  /** Map datos_ia (legacy OR new OCR schema) → FinancialData */
  const mapDatosIA = (ai: any): FinancialData => {
    // New OCR schema from buildTaxExtractionPrompt()
    const isNewSchema = !!ai.emisor?.nombre || !!ai.lineas_impuesto || !!ai.totales;

    // --- Emisor / Receptor ---
    const emisorNombre = isNewSchema ? ai.emisor?.nombre : ai.razonSocialEmisor;
    const emisorNif = isNewSchema ? ai.emisor?.nif : ai.nifEmisor;
    const emisorDir = isNewSchema ? ai.emisor?.direccion : ai.direccionEmisor;
    const receptorNombre = isNewSchema ? ai.receptor?.nombre : ai.razonSocialReceptor;
    const receptorNif = isNewSchema ? ai.receptor?.nif : ai.nifReceptor;
    const receptorDir = isNewSchema ? ai.receptor?.direccion : ai.direccionReceptor;

    // --- Tax lines ---
    let lineasIVA: LineaIVAForm[] = [];
    if (isNewSchema && ai.lineas_impuesto?.length) {
      lineasIVA = ai.lineas_impuesto.map((l: any) => ({
        tipo: l.porcentaje ?? 0,
        base: l.base_imponible ?? 0,
        cuota: l.cuota ?? 0,
        cuenta: l.cuenta || '',
      }));
    } else if (ai.lineasIVA?.length) {
      lineasIVA = ai.lineasIVA;
    }

    // --- Base / IVA / Total ---
    const baseImponible = isNewSchema ? ai.totales?.base_imponible : ai.baseImponible;
    const cuotaIVA = isNewSchema ? ai.totales?.total_impuestos : ai.cuotaIVA;
    const tipoIVA = lineasIVA.length === 1 ? lineasIVA[0].tipo : ai.tipoIVA;

    // --- Retenciones ---
    let porcentajeRet: number | undefined;
    let importeRet: number | undefined;
    let tipoRet = '';
    if (isNewSchema && ai.retenciones?.length) {
      const r = ai.retenciones[0];
      porcentajeRet = r.porcentaje;
      importeRet = r.cuota;
      tipoRet = r.tipo || '';
    } else {
      porcentajeRet = ai.porcentajeRetencion;
      importeRet = ai.importeRetencion;
      tipoRet = ai.tipoRetencion || '';
    }

    // --- Suplidos ---
    let suplidosTotal: number | undefined;
    let suplidosDetalle: SuplidoItem[] = [];
    if (isNewSchema && Array.isArray(ai.suplidos)) {
      suplidosDetalle = ai.suplidos.map((s: any) => ({ concepto: s.concepto || '', importe: s.importe ?? 0, nif_tercero: s.nif_tercero }));
      suplidosTotal = ai.totales?.total_suplidos ?? suplidosDetalle.reduce((s: number, i: SuplidoItem) => s + i.importe, 0);
    } else if (typeof ai.suplidos === 'number') {
      suplidosTotal = ai.suplidos;
    }

    // --- Line items (detailed breakdown) ---
    let lineasDetalle: LineaDetalle[] = [];
    if (isNewSchema && ai.lineas_detalle?.length) {
      lineasDetalle = ai.lineas_detalle.filter((l: any) => !l.es_suplido).map((l: any) => ({
        descripcion: l.descripcion || '',
        cantidad: l.cantidad,
        precio_unitario: l.precio_unitario,
        importe_linea: l.importe_linea,
        total: l.importe_linea,
      }));
    } else if (ai.lineasDetalle?.length) {
      lineasDetalle = ai.lineasDetalle;
    }

    // --- Totals ---
    const totalFactura = isNewSchema ? ai.totales?.total_factura : (ai.totalFactura || ai.importeTotal);
    const totalBruto = baseImponible != null && cuotaIVA != null && suplidosTotal != null
      ? baseImponible + cuotaIVA + suplidosTotal
      : baseImponible != null && cuotaIVA != null
        ? baseImponible + cuotaIVA
        : undefined;
    const totalNeto = totalFactura != null && importeRet != null
      ? totalFactura
      : undefined;
    const erpTotal = totalNeto ?? totalFactura;

    return {
      fechaFactura: isNewSchema ? ai.fecha_emision : (ai.fechaFactura || ai.fecha || ''),
      numeroFactura: isNewSchema ? ai.numero_documento : (ai.numeroFactura || ''),
      nifEmisor: emisorNif || '',
      razonSocialEmisor: emisorNombre || '',
      direccionEmisor: emisorDir || '',
      nifReceptor: receptorNif || '',
      razonSocialReceptor: receptorNombre || '',
      direccionReceptor: receptorDir || '',
      baseImponible,
      tipoIVA,
      cuotaIVA,
      totalFactura,
      tipoDocumento: isNewSchema ? ai.tipo_documento : (ai.tipoDocumento || ''),
      actividad: ai.actividad || '',
      fechaContable: ai.fechaContable || ai.fecha_operacion || '',
      fechaVencimiento: ai.fechaVencimiento || ai.fecha_vencimiento || '',
      porcentajeRetencion: porcentajeRet,
      importeRetencion: importeRet,
      tipoRetencion: tipoRet,
      lineasIVA,
      suplidos: suplidosTotal,
      suplidosDetalle,
      lineasDetalle,
      totalSuplidos: suplidosTotal,
      totalBruto,
      totalNeto,
      erpTotal,
      cuentaProveedor: ai.cuentaProveedor || '',
      cuentasGasto: ai.cuentasGasto || [],
      metodoPago: ai.metodoPago || ai.metodo_pago || '',
      nivelConfianza: ai.nivelConfianza ?? ai.confianza,
      tipoOperacion: ai.tipo_operacion || ai.tipoOperacion || '',
    };
  };

  const handleVerDocumento = (doc: DocumentoPendiente) => {
    setDocumentoActual(doc);
    if (doc.datos_ia) {
      setFinancialData(mapDatosIA(doc.datos_ia));
      setIsManualEntry(false);
    } else {
      setFinancialData({});
      setIsManualEntry(true);
    }
    setEditingFinancial(false);

    // Activate split-view
    const currentList = filteredDocumentos.length > 0 ? filteredDocumentos : allDocumentos;
    const idx = currentList.findIndex(d => d.id === doc.id);
    setDocumentosPendientesList(currentList);
    setIndiceDocumentoActual(idx >= 0 ? idx : 0);
    setMostrarSplitView(true);
  };

  const handleVerifyWithAI = async () => {
    if (!documentoActual) return;

    setVerifyingAI(true);
    try {
      const response = await fetch('/api/documents/verify-financial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          documentoId: documentoActual.id,
          forceReextract: true 
        }),
      });

      if (!response.ok) throw new Error('Error en verificacion IA');

      const { datosIA } = await response.json();
      
      setFinancialData(mapDatosIA(datosIA));
      
      setDocumentoActual({ ...documentoActual, datos_ia: datosIA, procesado_ia: true });
      await loadPendingDocuments();
      
      setIsManualEntry(false);
      toast.success('Datos financieros extraidos correctamente por IA');
    } catch (error: any) {
      console.error('Error verifying with AI:', error);
      toast.error(`Error al verificar con IA: ${error.message}`);
    } finally {
      setVerifyingAI(false);
    }
  };

  const handleSaveFinancialData = async () => {
    if (!documentoActual) return;

    setSavingFinancial(true);
    try {
      const datosIA = {
        ...(documentoActual.datos_ia || {}),
        fechaFactura: financialData.fechaFactura || undefined,
        numeroFactura: financialData.numeroFactura || undefined,
        nifEmisor: financialData.nifEmisor || undefined,
        razonSocialEmisor: financialData.razonSocialEmisor || undefined,
        nifReceptor: financialData.nifReceptor || undefined,
        razonSocialReceptor: financialData.razonSocialReceptor || undefined,
        baseImponible: financialData.baseImponible,
        tipoIVA: financialData.tipoIVA,
        cuotaIVA: financialData.cuotaIVA,
        totalFactura: financialData.totalFactura,
        nivelConfianza: documentoActual.datos_ia?.nivelConfianza || (isManualEntry ? 1.0 : 0.5),
        fecha: financialData.fechaFactura || undefined,
        importeTotal: financialData.totalFactura,
        proveedor: financialData.razonSocialEmisor || undefined,
        tipoDocumento: financialData.tipoDocumento || undefined,
        actividad: financialData.actividad || undefined,
        fechaContable: financialData.fechaContable || undefined,
        fechaVencimiento: financialData.fechaVencimiento || undefined,
        porcentajeRetencion: financialData.porcentajeRetencion,
        importeRetencion: financialData.importeRetencion,
        tipoRetencion: financialData.tipoRetencion || undefined,
        lineasIVA: financialData.lineasIVA?.length ? financialData.lineasIVA : undefined,
        suplidos: financialData.suplidos,
        cuentaProveedor: financialData.cuentaProveedor || undefined,
        cuentasGasto: financialData.cuentasGasto?.length ? financialData.cuentasGasto : undefined,
        metodoPago: financialData.metodoPago || undefined,
      };

      const response = await fetch('/api/documents/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentoId: documentoActual.id,
          updates: {
            datosIA: datosIA,
            procesadoIA: true,
          },
        }),
      });

      if (!response.ok) throw new Error('Error al guardar datos');

      const { data } = await response.json();
      setDocumentoActual({ ...documentoActual, datos_ia: datosIA, procesado_ia: true });
      await loadPendingDocuments();
      
      setEditingFinancial(false);
      toast.success('Datos financieros guardados correctamente');
    } catch (error: any) {
      console.error('Error saving financial data:', error);
      toast.error(`Error al guardar datos: ${error.message}`);
    } finally {
      setSavingFinancial(false);
    }
  };

  const calculateTotal = () => {
    if (financialData.baseImponible && financialData.cuotaIVA) {
      return financialData.baseImponible + financialData.cuotaIVA;
    }
    if (financialData.baseImponible && financialData.tipoIVA) {
      return financialData.baseImponible * (1 + financialData.tipoIVA / 100);
    }
    return financialData.totalFactura || 0;
  };

  const calculateIVA = () => {
    if (financialData.cuotaIVA) return financialData.cuotaIVA;
    if (financialData.baseImponible && financialData.tipoIVA) {
      return financialData.baseImponible * (financialData.tipoIVA / 100);
    }
    return 0;
  };

  const addLineaIVA = () => {
    setFinancialData(prev => ({
      ...prev,
      lineasIVA: [...(prev.lineasIVA || []), { tipo: 21, base: 0, cuota: 0 }]
    }));
  };

  const updateLineaIVA = (index: number, field: keyof LineaIVAForm, value: string | number) => {
    setFinancialData(prev => {
      const lineas = [...(prev.lineasIVA || [])];
      lineas[index] = { ...lineas[index], [field]: typeof value === 'string' && field !== 'cuenta' ? parseFloat(value) || 0 : value };
      return { ...prev, lineasIVA: lineas };
    });
  };

  const removeLineaIVA = (index: number) => {
    setFinancialData(prev => ({
      ...prev,
      lineasIVA: (prev.lineasIVA || []).filter((_, i) => i !== index)
    }));
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFileSelection(files);
    }
  };

  const handleFileSelection = (files: File[]) => {
    const validTypes = ['.pdf', '.xml', '.jpg', '.jpeg', '.png', '.xls', '.xlsx'];
    const validFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return validTypes.includes(extension);
    });

    if (validFiles.length !== files.length) {
      toast.error('Algunos archivos fueron ignorados. Solo se permiten PDF, Excel, XML, JPG y PNG');
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubirDocumentos = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const clienteId = uploadClienteId;
    const tipo = uploadTipo as 'emitida_pdf' | 'recibida_pdf' | 'emitida_excel' | 'recibida_excel' | 'otro';
    const notas = uploadNotas;

    if (!clienteId) {
      toast.error('Por favor selecciona un cliente');
      return;
    }

    if (!tipo) {
      toast.error('Por favor selecciona un tipo de documento');
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error('Por favor selecciona al menos un archivo');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    for (let i = 0; i < selectedFiles.length; i++) {
      if (selectedFiles[i].size > maxSize) {
        toast.error(`El archivo "${selectedFiles[i].name}" excede el tamano maximo de 10MB`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = selectedFiles.length;
      const uploadedIds: string[] = [];

      // Get session token once before the loop
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        setUploading(false);
        return;
      }

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('clienteId', clienteId);
        formData.append('tipo', tipo);
        if (notas) formData.append('notas', notas);
        if (user?.id) formData.append('asesorId', user.id);

        const response = await fetch('/api/documents/upload', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error al subir el documento');
        }

        const data = await response.json();
        uploadedIds.push(data.documentoId);
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      await loadPendingDocuments();

      toast.success(`${totalFiles} documento(s) subido(s) correctamente en nombre del cliente.`);
      setMostrarSubida(false);
      setSelectedFiles([]);
      setUploadClienteId('');
      setUploadTipo('');
      setUploadNotas('');
    } catch (error) {
      console.error('Error uploading documents:', error);
      
      let errorMessage = 'Error desconocido';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const supabaseError = error as any;
        if (supabaseError.message) {
          errorMessage = supabaseError.message;
        } else if (supabaseError.error_description) {
          errorMessage = supabaseError.error_description;
        }
      }
      
      toast.error(`Error al subir documentos: ${errorMessage}. Por favor, revisa el formato y tamano de los archivos e intentalo de nuevo.`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Helper function to calculate days ago
  const getDaysAgo = (date: Date | undefined): string => {
    if (!date) return 'N/A';
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'hoy';
    if (diffDays === 1) return 'hace 1 dia';
    return `hace ${diffDays} dias`;
  };

  // Helper function to get client initial
  const getClientInitial = (cliente: Cliente): string => {
    const name = cliente.nombre_comercial || cliente.razon_social || '';
    return name.charAt(0).toUpperCase() || '?';
  };

  // Handle Trabajar button click — activates split-view for client
  const handleTrabajar = (clienteId: string) => {
    const clientDocs = allDocumentos.filter(d => d.cliente_id === clienteId);
    if (clientDocs.length === 0) {
      toast.error('No hay documentos pendientes para este cliente');
      return;
    }
    setDocumentosPendientesList(clientDocs);
    setIndiceDocumentoActual(0);
    const firstDoc = clientDocs[0];
    setDocumentoActual(firstDoc);
    if (firstDoc.datos_ia) {
      setFinancialData(mapDatosIA(firstDoc.datos_ia));
      setIsManualEntry(false);
    } else {
      setFinancialData({});
      setIsManualEntry(true);
    }
    setEditingFinancial(false);
    setMostrarSplitView(true);
  };

  // Split-view: load financial data for a document
  const loadDocFinancialData = (doc: DocumentoPendiente) => {
    if (doc.datos_ia) {
      setFinancialData(mapDatosIA(doc.datos_ia));
      setIsManualEntry(false);
    } else {
      setFinancialData({});
      setIsManualEntry(true);
    }
    setEditingFinancial(false);
  };

  // Split-view: navigate between documents
  const handleSplitViewNavigate = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? indiceDocumentoActual - 1 : indiceDocumentoActual + 1;
    if (newIndex < 0 || newIndex >= documentosPendientesList.length) return;
    const doc = documentosPendientesList[newIndex];
    setIndiceDocumentoActual(newIndex);
    setDocumentoActual(doc);
    loadDocFinancialData(doc);
  };

  // Split-view: close and return to normal view
  const handleCloseSplitView = () => {
    setMostrarSplitView(false);
    setDocumentoActual(null);
    setEditingFinancial(false);
  };

  // Split-view: toggle collapsible sections
  const toggleSplitFormSection = (section: 'datos' | 'financieros') => {
    setSplitFormSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Extract IVA breakdown from document AI data
  const extractIVABreakdown = (doc: DocumentoPendiente): IVABreakdown => {
    const ai = doc.datos_ia;
    if (!ai) return {};

    if (ai.detallesIVA && Array.isArray(ai.detallesIVA)) {
      const result: IVABreakdown = {};
      for (const detalle of ai.detallesIVA) {
        const tipo = Number(detalle.tipo || detalle.tipoIVA || 0);
        const base = Number(detalle.base || detalle.baseImponible || 0);
        const cuota = Number(detalle.cuota || detalle.cuotaIVA || 0);
        if (tipo === 21) { result.baseIVA21 = base; result.iva21 = cuota; }
        else if (tipo === 10) { result.baseIVA10 = base; result.iva10 = cuota; }
        else if (tipo === 4) { result.baseIVA4 = base; result.iva4 = cuota; }
        else if (tipo === 0) { result.baseIVA0 = base; }
      }
      return result;
    }

    const tipoIVA = Number(ai.tipoIVA || 0);
    const base = ai.baseImponible !== undefined ? Number(ai.baseImponible) : undefined;
    const cuota = ai.cuotaIVA !== undefined ? Number(ai.cuotaIVA) : undefined;

    if (base === undefined) return {};

    if (tipoIVA === 21) return { baseIVA21: base, iva21: cuota };
    if (tipoIVA === 10) return { baseIVA10: base, iva10: cuota };
    if (tipoIVA === 4) return { baseIVA4: base, iva4: cuota };
    if (tipoIVA === 0) return { baseIVA0: base };

    return { baseIVA21: base, iva21: cuota };
  };

  // Format currency for table display
  const fmtCur = (val: number | undefined): string => {
    if (val === undefined || val === null || isNaN(val)) return '';
    return val.toFixed(2);
  };

  // Get status display label
  const getStatusLabel = (estado: string | undefined): string => {
    switch (estado) {
      case 'procesado_ia': return 'En proceso';
      case 'error': return 'Error';
      case 'pendiente': return 'Pendiente';
      case 'contabilizando': return 'Contabilizando';
      case 'contabilizado': return 'Contabilizado';
      case 'validado': return 'Validado';
      default: return estado || 'Desconocido';
    }
  };

  // Get status dot color
  const getStatusDotClass = (estado: string | undefined): string => {
    switch (estado) {
      case 'procesado_ia': return 'bg-blue-200';
      case 'error': return 'bg-red-200';
      case 'pendiente': return 'bg-orange-200';
      case 'contabilizando': return 'bg-sky-200';
      case 'contabilizado': case 'validado': return 'bg-slate-300';
      default: return 'bg-slate-200';
    }
  };

  // Filtered documents for table view
  const filteredDocumentos = useMemo(() => {
    if (statusFilter === 'todos') return allDocumentos;
    const activePill = STATUS_PILLS.find(p => p.key === statusFilter);
    if (!activePill || activePill.matchEstados.length === 0) return allDocumentos;
    return allDocumentos.filter(d => activePill.matchEstados.includes(d.estado || ''));
  }, [statusFilter, allDocumentos]);

  // Status counts for pills
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: allDocumentos.length };
    for (const pill of STATUS_PILLS) {
      if (pill.key !== 'todos') {
        counts[pill.key] = allDocumentos.filter(d =>
          pill.matchEstados.includes(d.estado || '')
        ).length;
      }
    }
    return counts;
  }, [allDocumentos]);

  // Select all in table
  const handleSelectAllTable = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(selectedDocs);
      filteredDocumentos.forEach(d => newSelected.add(d.id));
      setSelectedDocs(newSelected);
    } else {
      const newSelected = new Set(selectedDocs);
      filteredDocumentos.forEach(d => newSelected.delete(d.id));
      setSelectedDocs(newSelected);
    }
  };

  const allTableSelected = filteredDocumentos.length > 0 && filteredDocumentos.every(d => selectedDocs.has(d.id));

  // CSV Export
  const handleExportCSV = () => {
    if (filteredDocumentos.length === 0) {
      toast.error('No hay documentos para exportar');
      return;
    }

    const headers = [
      'Empresa', 'NIF', 'Nº Factura', 'Fecha', 'Estado',
      'Base IVA 21%', 'IVA 21%', 'Base IVA 10%', 'IVA 10%',
      'Base IVA 4%', 'IVA 4%', 'Base IVA 0%', 'Total'
    ];

    const rows = filteredDocumentos.map(doc => {
      const iva = extractIVABreakdown(doc);
      const total = doc.datos_ia?.totalFactura ?? doc.datos_ia?.importeTotal;
      return [
        `"${(doc.cliente?.nombre_comercial || doc.cliente?.razon_social || '').replace(/"/g, '""')}"`,
        `"${doc.cliente?.nif || ''}"`,
        `"${doc.datos_ia?.numeroFactura || ''}"`,
        `"${doc.datos_ia?.fechaFactura || doc.datos_ia?.fecha || ''}"`,
        `"${getStatusLabel(doc.estado)}"`,
        fmtCur(iva.baseIVA21),
        fmtCur(iva.iva21),
        fmtCur(iva.baseIVA10),
        fmtCur(iva.iva10),
        fmtCur(iva.baseIVA4),
        fmtCur(iva.iva4),
        fmtCur(iva.baseIVA0),
        total !== undefined ? Number(total).toFixed(2) : '',
      ].join(';');
    });

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documentos-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Archivo CSV exportado correctamente');
  };

  // PDF export placeholder
  const handleExportPDF = () => {
    const docsToExport = selectedDocs.size > 0
      ? filteredDocumentos.filter(d => selectedDocs.has(d.id))
      : filteredDocumentos;

    if (docsToExport.length === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    const headers = ['Nombre', 'Empresa', 'Tipo', 'Estado', 'Fecha'];
    const rows = docsToExport.map(doc => {
      const empresa = doc.cliente?.nombre_comercial || doc.cliente?.razon_social || '';
      const tipo = doc.tipo || '';
      const estado = getStatusLabel(doc.estado);
      const fecha = doc.fecha_subida
        ? new Date(doc.fecha_subida).toLocaleDateString('es-ES')
        : '';
      return [
        `"${(doc.nombre || '').replace(/"/g, '""')}"`,
        `"${empresa.replace(/"/g, '""')}"`,
        `"${tipo}"`,
        `"${estado}"`,
        `"${fecha}"`,
      ].join(';');
    });

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'documentos-pendientes-export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exportación completada: ${docsToExport.length} documento(s)`);
  };

  const totalPendientes = clientesConDocs.reduce((sum, c) => sum + c.documentos.length, 0);
  const clientesConPendientes = clientesConDocs.filter(c => c.documentos.length > 0).length;

  if (loading || loadingData) {
    return (
      <>
        <Head>
          <title>Documentos - Externaliza2</title>
        </Head>
        <Layout rol="asesor">
          <div className="px-12 pb-12 space-y-12 pt-8">
            <div className="flex justify-between items-end">
              <div className="space-y-3">
                <Skeleton className="h-12 w-96 rounded-huge" />
                <Skeleton className="h-5 w-56 rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Skeleton className="h-10 w-36 rounded-huge" />
                <Skeleton className="h-10 w-36 rounded-huge" />
                <Skeleton className="h-10 w-40 rounded-huge" />
              </div>
            </div>
            <div className="flex gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-32 rounded-full" />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-52 rounded-huge" />
              ))}
            </div>
            <Skeleton className="h-96 rounded-huge" />
          </div>
        </Layout>
      </>
    );
  }

  // Get status badge with vibrant colors
  const getStatusBadge = (estado: string | undefined): { bg: string; text: string; icon: string; border: string } => {
    switch (estado) {
      case 'procesado_ia': 
        return { 
          bg: 'bg-fresh-sky-100', 
          text: 'text-fresh-sky-700', 
          icon: 'text-fresh-sky-500',
          border: 'border-fresh-sky-300'
        };
      case 'error': 
        return { 
          bg: 'bg-red-100', 
          text: 'text-red-700', 
          icon: 'text-red-500',
          border: 'border-red-300'
        };
      case 'pendiente': 
        return { 
          bg: 'bg-amber-100', 
          text: 'text-amber-700', 
          icon: 'text-amber-500',
          border: 'border-amber-300'
        };
      case 'contabilizando': 
        return { 
          bg: 'bg-sky-100', 
          text: 'text-sky-700', 
          icon: 'text-sky-500',
          border: 'border-sky-300'
        };
      case 'contabilizado': 
        return { 
          bg: 'bg-emerald-100', 
          text: 'text-emerald-700', 
          icon: 'text-emerald-500',
          border: 'border-emerald-300'
        };
      case 'validado': 
        return { 
          bg: 'bg-teal-100', 
          text: 'text-teal-700', 
          icon: 'text-teal-500',
          border: 'border-teal-300'
        };
      default: 
        return { 
          bg: 'bg-slate-100', 
          text: 'text-slate-700', 
          icon: 'text-slate-500',
          border: 'border-slate-300'
        };
    }
  };

  return (
    <>
      <Head>
        <title>Documentos - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        {mostrarSplitView && documentoActual ? (
          /* ══════════════════════════════════════════════════════════════
             FULL-PAGE SPLIT VIEW — Document review with PDF viewer
             ══════════════════════════════════════════════════════════════ */
          <div className="h-[calc(100vh-72px)] flex flex-col bg-gradient-to-br from-fresh-sky-50/40 to-steel-azure-50/30">
            {/* ── Top Navigation Bar ── */}
            <div className="bg-white border-b border-calm-border px-6 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleCloseSplitView}
                  className="flex items-center gap-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                >
                  <Icon name="arrow_back" className="text-[20px]" />
                  <span>Volver</span>
                </button>
                <div className="h-5 w-px bg-calm-border" />
                <div>
                  <h2 className="font-display font-light text-calm-text text-lg leading-tight">Documento pendiente</h2>
                  <p className="text-xs font-light text-calm-muted">
                    {documentoActual.cliente?.razon_social || 'N/A'} &middot; {documentoActual.cliente?.nif || ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Document counter */}
                <div className="flex items-center gap-2 bg-calm-bg border border-calm-border rounded-huge px-4 py-2">
                  <button
                    onClick={() => handleSplitViewNavigate('prev')}
                    disabled={indiceDocumentoActual <= 0}
                    className="p-0.5 rounded-lg text-calm-muted hover:text-calm-text disabled:opacity-30 transition-colors"
                  >
                    <Icon name="navigate_before" className="text-[20px]" />
                  </button>
                  <span className="text-sm font-light text-calm-text tabular-nums min-w-[80px] text-center">
                    {indiceDocumentoActual + 1} / {documentosPendientesList.length}
                  </span>
                  <button
                    onClick={() => handleSplitViewNavigate('next')}
                    disabled={indiceDocumentoActual >= documentosPendientesList.length - 1}
                    className="p-0.5 rounded-lg text-calm-muted hover:text-calm-text disabled:opacity-30 transition-colors"
                  >
                    <Icon name="navigate_next" className="text-[20px]" />
                  </button>
                </div>

                {/* Quick actions */}
                <button
                  onClick={() => window.open(documentoActual.url_archivo, '_blank')}
                  className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                  title="Abrir en nueva pestaña"
                >
                  <Icon name="open_in_new" className="text-[20px]" />
                </button>
                <button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = documentoActual.url_archivo;
                    a.download = documentoActual.nombre;
                    a.click();
                  }}
                  className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                  title="Descargar"
                >
                  <Icon name="download" className="text-[20px]" />
                </button>
              </div>
            </div>

            {/* ── Split Content ── */}
            <div className="flex-1 overflow-hidden flex flex-row">
              {/* ── LEFT: PDF Viewer (~60%) ── */}
              <div className="w-[60%] min-w-0 h-full">
                <PdfViewer
                  fileUrl={documentoActual.url_archivo}
                  fileName={documentoActual.nombre}
                />
              </div>

              {/* ── RIGHT: Financial Data Panel (~40%) ── */}
              <div className="w-[40%] border-l border-calm-border bg-white flex flex-col overflow-hidden">
                {/* Form header */}
                <div className="px-6 py-4 border-b border-calm-border bg-calm-bg shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Datos del Documento</p>
                      <p className="text-sm font-light text-calm-text mt-0.5 truncate max-w-[280px]" title={documentoActual.nombre}>
                        {documentoActual.nombre}
                      </p>
                    </div>
                    <span className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-light border',
                      documentoActual.estado === 'procesado_ia' ? 'border-blue-200 text-blue-600 bg-blue-50' :
                      documentoActual.estado === 'pendiente' ? 'border-orange-200 text-orange-600 bg-orange-50' :
                      documentoActual.estado === 'error' ? 'border-red-200 text-red-600 bg-red-50' :
                      'border-calm-border text-calm-muted bg-white'
                    )}>
                      <span className={cn('w-2 h-2 rounded-full', getStatusDotClass(documentoActual.estado))} />
                      {getStatusLabel(documentoActual.estado)}
                    </span>
                  </div>
                  {documentoActual.datos_ia?.nivelConfianza && (
                    <p className="text-xs text-calm-muted font-light">
                      <span className={cn('font-medium',
                        documentoActual.datos_ia.nivelConfianza >= 0.8 ? 'text-emerald-600' :
                        documentoActual.datos_ia.nivelConfianza >= 0.6 ? 'text-amber-600' : 'text-red-500'
                      )}>
                        Confianza IA: {(documentoActual.datos_ia.nivelConfianza * 100).toFixed(0)}%
                      </span>
                    </p>
                  )}
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  {/* Progress indicator */}
                  <div className="bg-calm-bg border border-calm-border rounded-huge px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-light text-calm-text">
                      Documento {indiceDocumentoActual + 1} de {documentosPendientesList.length}
                    </span>
                    <span className="text-xs font-light text-calm-muted">
                      {documentosPendientesList.length - indiceDocumentoActual - 1} restante(s)
                    </span>
                  </div>

                  {/* Action buttons: Verify with AI / Manual Entry */}
                  {!editingFinancial && (
                    <div className="flex gap-2">
                      <button
                        className="flex-1 ghost-btn justify-center border-calm-text text-calm-text hover:bg-calm-bg hover:text-white"
                        onClick={handleVerifyWithAI}
                        disabled={verifyingAI}
                      >
                        <span className="material-symbols-outlined text-[18px]">smart_toy</span>
                        {verifyingAI ? 'Verificando...' : 'Verificar con IA'}
                      </button>
                      <button
                        className="flex-1 ghost-btn justify-center"
                        onClick={() => setEditingFinancial(true)}
                      >
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                        {documentoActual.datos_ia ? 'Editar' : 'Ingresar Manualmente'}
                      </button>
                    </div>
                  )}

                  {/* ── Section: Datos Financieros ── */}
                  <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge overflow-hidden">
                    <button
                      onClick={() => toggleSplitFormSection('financieros')}
                      className="flex items-center justify-between w-full px-5 py-3 bg-calm-bg hover:bg-calm-bg/70 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="payments" className="text-[18px] text-calm-accent" />
                        <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Datos Financieros</span>
                      </div>
                      <Icon name={splitFormSections.financieros ? 'expand_more' : 'chevron_right'} className="text-[18px] text-calm-muted" />
                    </button>
                    {splitFormSections.financieros && (
                      <div className="p-5">
                        {editingFinancial ? (
                          /* Manual Entry Form — Inmatic Style */
                          <div className="space-y-5">
                            {/* Group 1: Tipo y Actividad */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="category" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Tipo y Actividad</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Tipo Documento</label>
                                <Select value={financialData.tipoDocumento || ''} onValueChange={(val) => setFinancialData({ ...financialData, tipoDocumento: val })}>
                                  <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                                    <SelectValue placeholder="Seleccionar..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="factura_proveedor">Factura de proveedor</SelectItem>
                                    <SelectItem value="factura_cliente">Factura de cliente</SelectItem>
                                    <SelectItem value="factura_rectificativa">Factura rectificativa</SelectItem>
                                    <SelectItem value="recibo">Recibo</SelectItem>
                                    <SelectItem value="nomina">Nómina</SelectItem>
                                    <SelectItem value="otro">Otro</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Actividad</label>
                                <Select value={financialData.actividad || ''} onValueChange={(val) => setFinancialData({ ...financialData, actividad: val })}>
                                  <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                                    <SelectValue placeholder="Seleccionar..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="FACTURA">Factura</SelectItem>
                                    <SelectItem value="ABONO">Abono</SelectItem>
                                    <SelectItem value="RECTIFICATIVA">Rectificativa</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <Separator className="my-2" />

                            {/* Group 2: Identificación */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="badge" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Identificación</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">N&ordm; Factura</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.numeroFactura || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, numeroFactura: e.target.value })}
                                  placeholder="Ej: FAC-2024-001"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Factura</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="date"
                                  value={financialData.fechaFactura || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, fechaFactura: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Contable</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="date"
                                  value={financialData.fechaContable || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, fechaContable: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Vencimiento</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="date"
                                  value={financialData.fechaVencimiento || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, fechaVencimiento: e.target.value })}
                                />
                              </div>
                            </div>

                            <Separator className="my-2" />

                            {/* Group 3: Partes */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="people" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Partes</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">NIF Emisor</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.nifEmisor || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, nifEmisor: e.target.value })}
                                  placeholder="B12345678"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Razón Social Emisor</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.razonSocialEmisor || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, razonSocialEmisor: e.target.value })}
                                  placeholder="Nombre del emisor"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">NIF Receptor</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.nifReceptor || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, nifReceptor: e.target.value })}
                                  placeholder="A87654321"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Razón Social Receptor</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.razonSocialReceptor || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, razonSocialReceptor: e.target.value })}
                                  placeholder="Nombre del receptor"
                                />
                              </div>
                            </div>

                            <Separator className="my-2" />

                            {/* Group 4: Líneas IVA */}
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Icon name="receipt" className="text-[16px] text-calm-accent" />
                                <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Líneas IVA</span>
                              </div>
                              <button
                                type="button"
                                className="text-xs text-calm-accent hover:text-calm-text transition-colors flex items-center gap-1"
                                onClick={addLineaIVA}
                              >
                                <Icon name="add_circle" className="text-[16px]" />
                                Añadir línea
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(financialData.lineasIVA || []).map((linea, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_1.5fr_1.5fr_1.5fr_auto] gap-2 items-end">
                                  <div className="space-y-1">
                                    {idx === 0 && <label className="text-[9px] uppercase tracking-widest text-calm-muted font-medium">% IVA</label>}
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={linea.tipo || ''}
                                      onChange={(e) => updateLineaIVA(idx, 'tipo', e.target.value)}
                                      placeholder="21"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    {idx === 0 && <label className="text-[9px] uppercase tracking-widest text-calm-muted font-medium">Base (&euro;)</label>}
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={linea.base || ''}
                                      onChange={(e) => updateLineaIVA(idx, 'base', e.target.value)}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    {idx === 0 && <label className="text-[9px] uppercase tracking-widest text-calm-muted font-medium">Cuota (&euro;)</label>}
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={linea.cuota || ''}
                                      onChange={(e) => updateLineaIVA(idx, 'cuota', e.target.value)}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    {idx === 0 && <label className="text-[9px] uppercase tracking-widest text-calm-muted font-medium">Cuenta</label>}
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                      value={linea.cuenta || ''}
                                      onChange={(e) => updateLineaIVA(idx, 'cuenta', e.target.value)}
                                      placeholder="6290000"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="p-2 rounded-xl text-calm-muted hover:text-red-500 hover:bg-red-50 transition-all mb-0.5"
                                    onClick={() => removeLineaIVA(idx)}
                                  >
                                    <Icon name="close" className="text-[16px]" />
                                  </button>
                                </div>
                              ))}
                              {(!financialData.lineasIVA || financialData.lineasIVA.length === 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Base Imponible (&euro;)</label>
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={financialData.baseImponible || ''}
                                      onChange={(e) => {
                                        const base = parseFloat(e.target.value) || 0;
                                        const tipoIVA = financialData.tipoIVA || 21;
                                        const cuota = base * (tipoIVA / 100);
                                        setFinancialData({ ...financialData, baseImponible: base || undefined, cuotaIVA: cuota || undefined, totalFactura: (base + cuota) || undefined });
                                      }}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Tipo IVA (%)</label>
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={financialData.tipoIVA || ''}
                                      onChange={(e) => {
                                        const tipoIVA = parseFloat(e.target.value) || 0;
                                        const base = financialData.baseImponible || 0;
                                        const cuota = base * (tipoIVA / 100);
                                        setFinancialData({ ...financialData, tipoIVA: tipoIVA || undefined, cuotaIVA: cuota || undefined, totalFactura: (base + cuota) || undefined });
                                      }}
                                      placeholder="21"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Cuota IVA (&euro;)</label>
                                    <Input
                                      className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                      type="number" step="0.01"
                                      value={financialData.cuotaIVA || ''}
                                      onChange={(e) => {
                                        const cuota = parseFloat(e.target.value) || 0;
                                        const base = financialData.baseImponible || 0;
                                        setFinancialData({ ...financialData, cuotaIVA: cuota || undefined, totalFactura: (base + cuota) || undefined });
                                      }}
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <Separator className="my-2" />

                            {/* Group 5: Retenciones */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="percent" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Retenciones</span>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">% Retención</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="number" step="0.01"
                                  value={financialData.porcentajeRetencion ?? ''}
                                  onChange={(e) => setFinancialData({ ...financialData, porcentajeRetencion: parseFloat(e.target.value) || undefined })}
                                  placeholder="15"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Importe Ret. (&euro;)</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="number" step="0.01"
                                  value={financialData.importeRetencion ?? ''}
                                  onChange={(e) => setFinancialData({ ...financialData, importeRetencion: parseFloat(e.target.value) || undefined })}
                                  placeholder="0.00"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Tipo Ret.</label>
                                <Select value={financialData.tipoRetencion || ''} onValueChange={(val) => setFinancialData({ ...financialData, tipoRetencion: val })}>
                                  <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                                    <SelectValue placeholder="Tipo..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="IRPF">IRPF</SelectItem>
                                    <SelectItem value="IS">IS</SelectItem>
                                    <SelectItem value="otro">Otro</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <Separator className="my-2" />

                            {/* Group 6: Totales */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="calculate" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Totales</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Suplidos (&euro;)</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="number" step="0.01"
                                  value={financialData.suplidos ?? ''}
                                  onChange={(e) => setFinancialData({ ...financialData, suplidos: parseFloat(e.target.value) || undefined })}
                                  placeholder="0.00"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Total Factura (&euro;)</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-medium text-calm-text focus:border-calm-text/30 focus:ring-calm-text/20"
                                  type="number" step="0.01"
                                  value={financialData.totalFactura || calculateTotal()}
                                  onChange={(e) => setFinancialData({ ...financialData, totalFactura: parseFloat(e.target.value) || undefined })}
                                  placeholder="0.00"
                                />
                              </div>
                            </div>

                            <Separator className="my-2" />

                            {/* Group 7: Contabilidad */}
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="account_balance" className="text-[16px] text-calm-accent" />
                              <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Contabilidad</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Cuenta Proveedor</label>
                                <Input
                                  className="rounded-2xl border-calm-border text-sm font-light text-calm-text placeholder:text-calm-accent focus:border-calm-text/30 focus:ring-calm-text/20"
                                  value={financialData.cuentaProveedor || ''}
                                  onChange={(e) => setFinancialData({ ...financialData, cuentaProveedor: e.target.value })}
                                  placeholder="4000001"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Método de Pago</label>
                                <Select value={financialData.metodoPago || ''} onValueChange={(val) => setFinancialData({ ...financialData, metodoPago: val })}>
                                  <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                                    <SelectValue placeholder="Seleccionar..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Domiciliación">Domiciliación</SelectItem>
                                    <SelectItem value="Transferencia">Transferencia</SelectItem>
                                    <SelectItem value="Efectivo">Efectivo</SelectItem>
                                    <SelectItem value="Pagaré">Pagaré</SelectItem>
                                    <SelectItem value="Recibo">Recibo</SelectItem>
                                    <SelectItem value="otro">Otro</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="bg-slate-50 border border-calm-border rounded-2xl p-4 mt-2">
                              <p className="text-sm text-calm-muted font-light">
                                <span className="material-symbols-outlined text-[16px] align-middle mr-1 text-calm-accent">lightbulb</span>
                                Usa las líneas IVA para detallar cada tipo impositivo. Si no hay líneas, ingresa base y tipo IVA directamente.
                              </p>
                            </div>

                            <div className="flex gap-2">
                              <button
                                className="flex-1 ghost-btn justify-center border-calm-text text-calm-text hover:bg-calm-text hover:text-white"
                                onClick={handleSaveFinancialData}
                                disabled={savingFinancial}
                              >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                {savingFinancial ? 'Guardando...' : 'Guardar Datos'}
                              </button>
                              <button
                                className="flex-1 ghost-btn justify-center"
                                onClick={() => {
                                  if (documentoActual.datos_ia) {
                                    loadDocFinancialData(documentoActual);
                                  }
                                  setEditingFinancial(false);
                                }}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Display Financial Data — Inmatic Style */
                          <div className="space-y-4">
                            {(!documentoActual.datos_ia || Object.keys(financialData).length === 0) ? (
                              <div className="text-center py-12 bg-slate-50 rounded-2xl border border-calm-border">
                                <span className="material-symbols-outlined text-[40px] text-calm-accent mb-3 block">receipt_long</span>
                                <p className="text-calm-muted font-light mb-2">No hay datos financieros disponibles</p>
                                <p className="text-sm text-calm-accent font-light">
                                  Usa &quot;Verificar con IA&quot; para extraer automaticamente o &quot;Ingresar Manualmente&quot; para agregar los datos.
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {/* ═══ TABLE 1: Datos Consolidados de la Factura ═══ */}
                                <div className="bg-slate-50 border border-calm-border/50 rounded-2xl overflow-hidden">
                                  <div className="px-4 py-2.5 border-b border-calm-border/30 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[16px] text-steel-azure-500">description</span>
                                    <span className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Datos de la Factura</span>
                                  </div>
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-calm-border/20">
                                        <th className="px-4 py-2 text-left text-[10px] uppercase tracking-widest text-calm-muted font-medium w-[30%]">Categoría</th>
                                        <th className="px-4 py-2 text-left text-[10px] uppercase tracking-widest text-calm-muted font-medium w-[35%]">Descripción</th>
                                        <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Valor</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {/* Document identification */}
                                      {financialData.tipoDocumento && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Factura</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Tipo de documento</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light">{financialData.tipoDocumento.replace(/_/g, ' ')}</td>
                                        </tr>
                                      )}
                                      {financialData.numeroFactura && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Factura</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Nº de factura</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.numeroFactura}</td>
                                        </tr>
                                      )}
                                      {financialData.fechaFactura && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Factura</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Fecha de factura</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light">{(() => { try { return new Date(financialData.fechaFactura!).toLocaleDateString('es-ES'); } catch { return financialData.fechaFactura; } })()}</td>
                                        </tr>
                                      )}
                                      {/* Emisor */}
                                      {financialData.razonSocialEmisor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Emisor</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Razón social</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light">{financialData.razonSocialEmisor}</td>
                                        </tr>
                                      )}
                                      {financialData.direccionEmisor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Emisor</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Dirección</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light text-xs">{financialData.direccionEmisor}</td>
                                        </tr>
                                      )}
                                      {financialData.nifEmisor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Emisor</td>
                                          <td className="px-4 py-2 text-calm-text font-light">NIF / CIF</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.nifEmisor}</td>
                                        </tr>
                                      )}
                                      {/* Receptor / Interesado */}
                                      {financialData.razonSocialReceptor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Interesado</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Razón social</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light">{financialData.razonSocialReceptor}</td>
                                        </tr>
                                      )}
                                      {financialData.direccionReceptor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Interesado</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Dirección</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light text-xs">{financialData.direccionReceptor}</td>
                                        </tr>
                                      )}
                                      {financialData.nifReceptor && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Interesado</td>
                                          <td className="px-4 py-2 text-calm-text font-light">NIF / CIF</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.nifReceptor}</td>
                                        </tr>
                                      )}
                                      {/* Financial summary rows */}
                                      {financialData.baseImponible != null && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Base</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Base imponible</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.baseImponible.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.cuotaIVA != null && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">IVA</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Cuota IVA</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.cuotaIVA.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.totalSuplidos != null && financialData.totalSuplidos > 0 && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Total Suplidos</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Desembolsos</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.totalSuplidos.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.totalBruto != null && (
                                        <tr className="border-b border-calm-border/10">
                                          <td className="px-4 py-2 text-calm-muted font-light">Total</td>
                                          <td className="px-4 py-2 text-calm-text font-light">Total bruto</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.totalBruto.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.importeRetencion != null && (
                                        <tr className="border-b border-calm-border/10 bg-amber-50/40">
                                          <td className="px-4 py-2 text-amber-700 font-light">Retención</td>
                                          <td className="px-4 py-2 text-amber-700 font-light">Retención {financialData.porcentajeRetencion ? `(${financialData.porcentajeRetencion}%)` : ''}</td>
                                          <td className="px-4 py-2 text-right text-amber-700 font-light font-mono">-{financialData.importeRetencion.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.totalFactura != null && (
                                        <tr className="border-b border-calm-border/10 bg-fresh-sky-50/40">
                                          <td className="px-4 py-2 text-fresh-sky-700 font-medium">Final a pagar</td>
                                          <td className="px-4 py-2 text-fresh-sky-700 font-light">Importe neto</td>
                                          <td className="px-4 py-2 text-right text-fresh-sky-700 font-medium font-mono">{financialData.totalFactura.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                      {financialData.erpTotal != null && (
                                        <tr className="bg-steel-azure-50/40">
                                          <td className="px-4 py-2 text-steel-azure-700 font-medium">ERP Total</td>
                                          <td className="px-4 py-2 text-steel-azure-700 font-light">Total para ERP (Cegid)</td>
                                          <td className="px-4 py-2 text-right text-steel-azure-700 font-medium font-mono">{financialData.erpTotal.toFixed(2)} €</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {/* ═══ IVA Breakdown (compact) ═══ */}
                                <div className="bg-slate-50 border border-calm-border/50 rounded-2xl overflow-hidden">
                                  <div className="px-4 py-2.5 border-b border-calm-border/30 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[16px] text-fresh-sky-500">percent</span>
                                    <span className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Desglose IVA</span>
                                  </div>
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-calm-border/20">
                                        <th className="px-4 py-2 text-left text-[10px] uppercase tracking-widest text-calm-muted font-medium">Tipo (%)</th>
                                        <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Base (€)</th>
                                        <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Cuota (€)</th>
                                        <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Cuenta</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(financialData.lineasIVA && financialData.lineasIVA.length > 0) ? (
                                        financialData.lineasIVA.map((linea, idx) => (
                                          <tr key={idx} className="border-b border-calm-border/10 last:border-0">
                                            <td className="px-4 py-2 text-calm-text font-light">{linea.tipo}%</td>
                                            <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{linea.base.toFixed(2)}</td>
                                            <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{linea.cuota.toFixed(2)}</td>
                                            <td className="px-4 py-2 text-right text-calm-muted font-light text-xs">{linea.cuenta || '-'}</td>
                                          </tr>
                                        ))
                                      ) : (financialData.baseImponible != null) ? (
                                        <tr>
                                          <td className="px-4 py-2 text-calm-text font-light">{financialData.tipoIVA || 0}%</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.baseImponible?.toFixed(2)}</td>
                                          <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{financialData.cuotaIVA?.toFixed(2) || '0.00'}</td>
                                          <td className="px-4 py-2 text-right text-calm-muted font-light">-</td>
                                        </tr>
                                      ) : (
                                        <tr><td colSpan={4} className="px-4 py-3 text-center text-calm-muted font-light text-xs">Sin datos de IVA</td></tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {/* ═══ TABLE 2: Desglose de Conceptos (Line Items) ═══ */}
                                {financialData.lineasDetalle && financialData.lineasDetalle.length > 0 && (
                                  <div className="bg-slate-50 border border-calm-border/50 rounded-2xl overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-calm-border/30 flex items-center gap-2">
                                      <span className="material-symbols-outlined text-[16px] text-emerald-500">list_alt</span>
                                      <span className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Desglose de Conceptos</span>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b border-calm-border/20">
                                            <th className="px-4 py-2 text-left text-[10px] uppercase tracking-widest text-calm-muted font-medium">Descripción</th>
                                            <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Cant.</th>
                                            <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Total (€)</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {financialData.lineasDetalle.map((linea, idx) => (
                                            <tr key={idx} className="border-b border-calm-border/10 last:border-0">
                                              <td className="px-4 py-2 text-calm-text font-light text-xs leading-relaxed">{linea.descripcion}</td>
                                              <td className="px-4 py-2 text-right text-calm-muted font-light tabular-nums">{linea.cantidad ?? '-'}</td>
                                              <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{(linea.total ?? linea.importe_linea)?.toFixed(2) ?? '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* ═══ TABLE 3: Suplidos (Disbursements) ═══ */}
                                {financialData.suplidosDetalle && financialData.suplidosDetalle.length > 0 && (
                                  <div className="bg-amber-50/50 border border-amber-200/50 rounded-2xl overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-amber-200/30 flex items-center gap-2">
                                      <span className="material-symbols-outlined text-[16px] text-amber-600">receipt</span>
                                      <span className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Suplidos (Desembolsos)</span>
                                    </div>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-amber-200/20">
                                          <th className="px-4 py-2 text-left text-[10px] uppercase tracking-widest text-calm-muted font-medium">Concepto</th>
                                          <th className="px-4 py-2 text-right text-[10px] uppercase tracking-widest text-calm-muted font-medium">Importe (€)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {financialData.suplidosDetalle.map((s, idx) => (
                                          <tr key={idx} className="border-b border-amber-200/10 last:border-0">
                                            <td className="px-4 py-2 text-calm-text font-light text-xs">{s.concepto}</td>
                                            <td className="px-4 py-2 text-right text-calm-text font-light font-mono">{s.importe.toFixed(2)}</td>
                                          </tr>
                                        ))}
                                        {financialData.totalSuplidos != null && (
                                          <tr className="border-t border-amber-200/40 bg-amber-100/30">
                                            <td className="px-4 py-2 text-amber-800 font-medium text-xs">TOTAL SUPLIDOS</td>
                                            <td className="px-4 py-2 text-right text-amber-800 font-medium font-mono">{financialData.totalSuplidos.toFixed(2)}</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {/* ═══ Single suplidos total (legacy — no breakdown) ═══ */}
                                {(!financialData.suplidosDetalle || financialData.suplidosDetalle.length === 0) && financialData.suplidos != null && financialData.suplidos > 0 && (
                                  <div className="bg-amber-50/50 border border-amber-200/50 p-4 rounded-2xl">
                                    <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Total Suplidos</div>
                                    <div className="text-2xl font-display font-light text-calm-text tabular-nums">{financialData.suplidos.toFixed(2)} €</div>
                                  </div>
                                )}

                                {/* ═══ Retenciones detail (legacy cards) ═══ */}
                                {(financialData.porcentajeRetencion || financialData.importeRetencion) && (
                                  <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-amber-50 border border-amber-200/50 p-3 rounded-2xl">
                                      <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">% Retención</div>
                                      <div className="text-calm-text font-light">{financialData.porcentajeRetencion ?? '-'}%</div>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200/50 p-3 rounded-2xl">
                                      <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Retención</div>
                                      <div className="text-calm-text font-light font-mono">{financialData.importeRetencion?.toFixed(2) ?? '-'} €</div>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200/50 p-3 rounded-2xl">
                                      <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Tipo Ret.</div>
                                      <div className="text-calm-text font-light">{financialData.tipoRetencion || '-'}</div>
                                    </div>
                                  </div>
                                )}

                                {/* ═══ Accounting / Payment info ═══ */}
                                {(financialData.cuentaProveedor || financialData.metodoPago || (financialData.cuentasGasto && financialData.cuentasGasto.length > 0)) && (
                                  <div className="grid grid-cols-2 gap-3">
                                    {financialData.metodoPago && (
                                      <div className="bg-slate-50 border border-calm-border/50 p-3 rounded-2xl">
                                        <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Método de Pago</div>
                                        <div className="text-calm-text font-light text-sm">{financialData.metodoPago}</div>
                                      </div>
                                    )}
                                    {financialData.cuentaProveedor && (
                                      <div className="bg-slate-50 border border-calm-border/50 p-3 rounded-2xl">
                                        <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Cuenta Proveedor</div>
                                        <div className="text-calm-text font-light font-mono text-sm">{financialData.cuentaProveedor}</div>
                                      </div>
                                    )}
                                    {financialData.cuentasGasto && financialData.cuentasGasto.length > 0 && (
                                      <div className="bg-slate-50 border border-calm-border/50 p-3 rounded-2xl col-span-2">
                                        <div className="text-[10px] uppercase tracking-widest text-calm-muted font-medium mb-1">Cuentas de Gasto</div>
                                        <div className="text-calm-text font-light font-mono text-sm">{financialData.cuentasGasto.join(', ')}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Action Buttons (fixed at bottom) ── */}
                <div className="px-6 py-4 border-t border-calm-border bg-white shrink-0 space-y-3">
                  {/* Primary: Save / Contabilizar */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleSaveFinancialData}
                      className="border border-calm-border rounded-huge px-4 py-2.5 text-sm font-light text-calm-text hover:bg-calm-bg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40"
                      disabled={savingFinancial || Object.keys(financialData).length === 0}
                    >
                      <Icon name="save" className="text-[16px]" />
                      {savingFinancial ? 'Guardando...' : 'Guardar Datos'}
                    </button>
                    <button
                      onClick={() => documentoActual?.id && handlePostAutomaticallySingle(documentoActual.id)}
                      className="bg-gradient-to-r from-fresh-sky-500 to-fresh-sky-600 text-white font-medium rounded-huge px-4 py-2.5 hover:from-fresh-sky-600 hover:to-fresh-sky-700 hover:shadow-md transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">smart_toy</span>
                      Contabilizar Auto
                    </button>
                  </div>

                  {/* Secondary actions */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {selectedDocs.size > 0 && (
                      <>
                        <span className="text-sm text-calm-muted font-light">{selectedDocs.size} seleccionado(s)</span>
                        <button className="ghost-btn border-calm-text text-calm-text" onClick={handlePostAutomatically}>
                          <span className="material-symbols-outlined text-[18px]">smart_toy</span>
                          Contabilizar Auto
                        </button>
                        <button
                          className="border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors inline-flex items-center gap-2"
                          onClick={handleDownloadZip}
                        >
                          <span className="material-symbols-outlined text-[18px]">download</span>
                          Descargar ZIP
                        </button>
                        <button
                          className="border border-emerald-200 rounded-huge px-4 py-2 text-sm font-light text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors inline-flex items-center gap-2"
                          onClick={handleBulkPuntear}
                        >
                          <span className="material-symbols-outlined text-[18px]">check</span>
                          Marcar Punteado
                        </button>
                        <button
                          className="border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                          onClick={handleBulkDesPuntear}
                        >
                          Desmarcar Punteado
                        </button>
                        <button className="ghost-btn border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600" onClick={handleBulkDelete}>
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                          Eliminar
                        </button>
                        <button
                          className="text-sm font-light text-calm-accent hover:text-calm-text transition-colors"
                          onClick={() => setSelectedDocs(new Set())}
                        >
                          Deseleccionar
                        </button>
                      </>
                    )}
                    {/* ACCIONES dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-fresh-sky-500 to-fresh-sky-600 text-white text-sm font-medium rounded-huge hover:from-fresh-sky-600 hover:to-fresh-sky-700 hover:shadow-md transition-all duration-200">
                          <span className="material-symbols-outlined text-[16px]">bolt</span>
                          ACCIONES
                          <span className="material-symbols-outlined text-[14px]">expand_more</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 rounded-2xl border-calm-border shadow-lg">
                        <DropdownMenuItem
                          className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => {
                            if (selectedDocs.size === 0) { toast.error('Selecciona al menos un documento'); return; }
                            toast.promise(Promise.resolve(), { loading: 'Reintentando...', success: 'Reintentado', error: 'Error' });
                          }}
                        >
                          <span className="material-symbols-outlined text-[16px] text-fresh-sky-500">refresh</span>
                          Reintentar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 text-sm font-light cursor-pointer rounded-xl text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() => {
                            if (selectedDocs.size === 0) { toast.error('Selecciona al menos un documento'); return; }
                            handleBulkDelete();
                          }}
                        >
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                          Borrar Documentos
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => handleMergeDocuments()}
                        >
                          <span className="material-symbols-outlined text-[16px] text-steel-azure-500">merge</span>
                          Unir Documentos
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => handleSplitPages()}  
                        >
                          <span className="material-symbols-outlined text-[16px] text-steel-azure-500">call_split</span>
                          Separar Páginas
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="gap-2 text-sm font-light rounded-xl">
                            <span className="material-symbols-outlined text-[16px] text-calm-accent">download</span>
                            Descargas
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-48 rounded-2xl border-calm-border">
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={handleExportCSV}
                            >
                              <span className="material-symbols-outlined text-[16px]">table_chart</span>
                              Exportar Excel
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={handleExportPDF}
                            >
                              <span className="material-symbols-outlined text-[16px]">picture_as_pdf</span>
                              Descargar PDF
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-calm-accent px-2">Acciones Masivas</DropdownMenuLabel>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="gap-2 text-sm font-light rounded-xl">
                            <span className="material-symbols-outlined text-[16px] text-calm-accent">tune</span>
                            Cambios Masivos
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-52 rounded-2xl border-calm-border">
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => {
                                if (selectedDocs.size === 0) { toast.error('Selecciona documentos primero'); return; }
                                setMostrarCambiosMasivos(true);
                              }}
                            >
                              <span className="material-symbols-outlined text-[16px]">edit_calendar</span>
                              Cambios de Campos
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => { if (selectedDocs.size === 0) { toast.error("Selecciona documentos primero"); return; } setMostrarCambiarEmpresa(true); }}  
                            >
                              <span className="material-symbols-outlined text-[16px]">business</span>
                              Cambiar Empresa
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => { if (selectedDocs.size === 0) { toast.error("Selecciona documentos primero"); return; } setMostrarAsignarTercero(true); }}  
                            >
                              <span className="material-symbols-outlined text-[16px]">person_add</span>
                              Asignar Tercero
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => handleRotateDocuments()}  
                            >
                              <span className="material-symbols-outlined text-[16px]">rotate_right</span>
                              Rotar Documentos
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => handleCalcularBaseYCuota()}  
                            >
                              <span className="material-symbols-outlined text-[16px]">calculate</span>
                              Calcular Base y Cuota
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                              onClick={() => handleRedondeo2Decimales()}  
                            >
                              <span className="material-symbols-outlined text-[16px]">decimal_increase</span>
                              Redondeo 2 decimales
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="h-[calc(100vh-72px)] flex flex-col overflow-hidden">
          {/* ── Main scrollable content ── */}
          <div className="flex-1 overflow-y-auto px-12 pb-12 space-y-12 pt-8">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="page-title">Documentos</h1>
              <p className="text-calm-muted font-light mt-3 text-base">
                {totalPendientes} documento(s) pendiente(s) de {clientesConPendientes} cliente(s)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="ghost-btn hover:bg-fresh-sky-50 hover:text-fresh-sky-600 hover:border-fresh-sky-300" onClick={handleExportCSV}>
                <span className="material-symbols-outlined text-[18px] text-fresh-sky-500">table_chart</span>
                Exportar Excel
              </button>
              <button className="ghost-btn hover:bg-fresh-sky-50 hover:text-fresh-sky-600 hover:border-fresh-sky-300" onClick={handleExportPDF}>
                <span className="material-symbols-outlined text-[18px] text-fresh-sky-500">description</span>
                Descargar PDF
              </button>
              <button
                className="bg-gradient-to-r from-fresh-sky-500 to-fresh-sky-600 text-white font-medium rounded-huge px-5 py-2.5 hover:from-fresh-sky-600 hover:to-fresh-sky-700 hover:shadow-md transition-all duration-300 flex items-center gap-2"
                onClick={() => setMostrarSubida(true)}
              >
                <span className="material-symbols-outlined text-[18px]">upload</span>
                Subir Documentos
              </button>
            </div>
          </div>

          {/* Status Filter Pills */}
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_PILLS.map((pill) => {
              const count = statusCounts[pill.key] ?? 0;
              const isActive = statusFilter === pill.key;
              return (
                <button
                  key={pill.key}
                  className={cn(
                    'px-5 py-2 rounded-full text-sm font-light border transition-colors inline-flex items-center gap-2',
                    isActive
                      ? 'border-calm-text text-calm-text'
                      : 'bg-white border-calm-border text-calm-muted hover:border-calm-text hover:text-calm-text'
                  )}
                  onClick={() => setStatusFilter(pill.key)}
                >
                  {pill.key !== 'todos' && pill.matchEstados.length > 0 && (
                    <span className={cn('w-2 h-2 rounded-full', getEstadoInfo(pill.matchEstados[0]).dot)} />
                  )}
                  <span>{pill.label}</span>
                  <span className={cn('text-[10px] tabular-nums', isActive ? 'opacity-60' : 'text-calm-accent')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Documents Table */}
          <div className="bg-white border-2 border-fresh-sky-200 rounded-huge overflow-hidden shadow-sm">
            <div className="px-8 py-6 border-b border-calm-border flex items-center justify-between">
              <div>
                <h2 className="section-title">
                  Detalle de Documentos
                </h2>
                <p className="text-sm text-calm-muted font-light mt-1">
                  {filteredDocumentos.length} documento{filteredDocumentos.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {selectedDocs.size > 0 && (
                  <>
                    <span className="text-sm text-calm-muted font-light">{selectedDocs.size} seleccionado(s)</span>
                    <button className="ghost-btn border-calm-text text-calm-text" onClick={handlePostAutomatically}>
                      <span className="material-symbols-outlined text-[18px]">smart_toy</span>
                      Contabilizar Auto
                    </button>
                    <button
                      className="border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors inline-flex items-center gap-2"
                      onClick={handleDownloadZip}
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Descargar ZIP
                    </button>
                    <button
                      className="border border-emerald-200 rounded-huge px-4 py-2 text-sm font-light text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors inline-flex items-center gap-2"
                      onClick={handleBulkPuntear}
                    >
                      <span className="material-symbols-outlined text-[18px]">check</span>
                      Marcar Punteado
                    </button>
                    <button
                      className="border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                      onClick={handleBulkDesPuntear}
                    >
                      Desmarcar Punteado
                    </button>
                    <button className="ghost-btn border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600" onClick={handleBulkDelete}>
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                      Eliminar
                    </button>
                    <button
                      className="text-sm font-light text-calm-accent hover:text-calm-text transition-colors"
                      onClick={() => setSelectedDocs(new Set())}
                    >
                      Deseleccionar
                    </button>
                  </>
                )}
                {/* ACCIONES dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-fresh-sky-500 to-fresh-sky-600 text-white text-sm font-medium rounded-huge hover:from-fresh-sky-600 hover:to-fresh-sky-700 hover:shadow-md transition-all duration-200">
                      <span className="material-symbols-outlined text-[16px]">bolt</span>
                      ACCIONES
                      <span className="material-symbols-outlined text-[14px]">expand_more</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-2xl border-calm-border shadow-lg">
                    <DropdownMenuItem
                      className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                      onClick={() => {
                        if (selectedDocs.size === 0) { toast.error('Selecciona al menos un documento'); return; }
                        toast.promise(Promise.resolve(), { loading: 'Reintentando...', success: 'Reintentado', error: 'Error' });
                      }}
                    >
                      <span className="material-symbols-outlined text-[16px] text-fresh-sky-500">refresh</span>
                      Reintentar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2 text-sm font-light cursor-pointer rounded-xl text-red-600 focus:text-red-600 focus:bg-red-50"
                      onClick={() => {
                        if (selectedDocs.size === 0) { toast.error('Selecciona al menos un documento'); return; }
                        handleBulkDelete();
                      }}
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                      Borrar Documentos
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                      onClick={() => handleMergeDocuments()}
                    >
                      <span className="material-symbols-outlined text-[16px] text-steel-azure-500">merge</span>
                      Unir Documentos
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                      onClick={() => handleSplitPages()}  
                    >
                      <span className="material-symbols-outlined text-[16px] text-steel-azure-500">call_split</span>
                      Separar Páginas
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="gap-2 text-sm font-light rounded-xl">
                        <span className="material-symbols-outlined text-[16px] text-calm-accent">download</span>
                        Descargas
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48 rounded-2xl border-calm-border">
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={handleExportCSV}
                        >
                          <span className="material-symbols-outlined text-[16px]">table_chart</span>
                          Exportar Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={handleExportPDF}
                        >
                          <span className="material-symbols-outlined text-[16px]">picture_as_pdf</span>
                          Descargar PDF
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-calm-accent px-2">Acciones Masivas</DropdownMenuLabel>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="gap-2 text-sm font-light rounded-xl">
                        <span className="material-symbols-outlined text-[16px] text-calm-accent">tune</span>
                        Cambios Masivos
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52 rounded-2xl border-calm-border">
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => {
                            if (selectedDocs.size === 0) { toast.error('Selecciona documentos primero'); return; }
                            setMostrarCambiosMasivos(true);
                          }}
                        >
                          <span className="material-symbols-outlined text-[16px]">edit_calendar</span>
                          Cambios de Campos
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => { if (selectedDocs.size === 0) { toast.error("Selecciona documentos primero"); return; } setMostrarCambiarEmpresa(true); }}  
                        >
                          <span className="material-symbols-outlined text-[16px]">business</span>
                          Cambiar Empresa
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => { if (selectedDocs.size === 0) { toast.error("Selecciona documentos primero"); return; } setMostrarAsignarTercero(true); }}  
                        >
                          <span className="material-symbols-outlined text-[16px]">person_add</span>
                          Asignar Tercero
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => handleRotateDocuments()}  
                        >
                          <span className="material-symbols-outlined text-[16px]">rotate_right</span>
                          Rotar Documentos
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => handleCalcularBaseYCuota()}  
                        >
                          <span className="material-symbols-outlined text-[16px]">calculate</span>
                          Calcular Base y Cuota
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 text-sm font-light cursor-pointer rounded-xl"
                          onClick={() => handleRedondeo2Decimales()}  
                        >
                          <span className="material-symbols-outlined text-[16px]">decimal_increase</span>
                          Redondeo 2 decimales
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-steel-azure-50/50 to-fresh-sky-50/30 border-b border-calm-border">
                  <TableHead className="w-10 px-4">
                    <Checkbox
                      checked={allTableSelected}
                      onCheckedChange={(checked) => handleSelectAllTable(!!checked)}
                    />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text" onClick={() => handleSort('documento')}>
                    <div className="flex items-center"><span>Documento</span><SortArrow col="documento" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text" onClick={() => handleSort('cliente')}>
                    <div className="flex items-center"><span>Cliente</span><SortArrow col="cliente" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text" onClick={() => handleSort('estado')}>
                    <div className="flex items-center"><span>Estado</span><SortArrow col="estado" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text" onClick={() => handleSort('asiento')}>
                    <div className="flex items-center"><span>Nº Asiento</span><SortArrow col="asiento" /></div>
                  </TableHead>
                  <TableHead className="text-center cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text w-20" onClick={() => handleSort('punteado')}>
                    <div className="flex items-center justify-center"><span>Punteado</span><SortArrow col="punteado" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text" onClick={() => handleSort('fecha')}>
                    <div className="flex items-center"><span>Fecha</span><SortArrow col="fecha" /></div>
                  </TableHead>
                  <TableHead className="text-right text-[11px] uppercase tracking-widest text-calm-muted pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSortedDocumentos(filteredDocumentos).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-16 text-center text-calm-muted font-light">
                      No hay documentos para mostrar
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedDocumentos(filteredDocumentos).map((doc, index) => {
                    const estadoInfo = getEstadoInfo(doc.estado || '');
                    const isPunteado = punteados.has(doc.id);
                    const isEven = index % 2 === 0;
                    return (
                      <TableRow
                        key={doc.id}
                        className={cn(
                          'group hover:bg-fresh-sky-50/70 transition-colors border-b border-calm-border/50',
                          isEven && 'bg-gradient-to-r from-fresh-sky-50/30 to-transparent',
                          selectedDocs.has(doc.id) && 'bg-fresh-sky-100/50'
                        )}
                      >
                        <TableCell className="px-4">
                          <Checkbox
                            checked={selectedDocs.has(doc.id)}
                            onCheckedChange={() => toggleDocSelection(doc.id)}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-calm-text font-light max-w-[180px]" title={doc.nombre}>
                          <div className="flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[16px] text-calm-accent shrink-0">description</span>
                            <span className="truncate">{doc.nombre}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-calm-muted font-light max-w-[140px] truncate" title={doc.cliente?.razon_social}>
                          {doc.cliente?.nombre_comercial || doc.cliente?.razon_social || '-'}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-xs font-light text-calm-muted">
                            <span className={cn('w-2 h-2 rounded-full shrink-0', estadoInfo.dot)} />
                            {estadoInfo.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs font-light text-calm-muted tabular-nums">
                          {(doc as any).nro_asiento_erp || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            className={cn(
                              'h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors',
                              isPunteado
                                ? 'bg-emerald-50 text-emerald-500 ring-1 ring-emerald-200 hover:bg-emerald-100'
                                : 'bg-slate-50 text-calm-accent hover:bg-slate-100 hover:text-calm-muted'
                            )}
                            onClick={() => togglePunteado(doc.id)}
                            title={isPunteado ? 'Desmarcar punteado' : 'Marcar punteado'}
                          >
                            <span className="material-symbols-outlined text-[16px]">check</span>
                          </button>
                        </TableCell>
                        <TableCell className="text-xs font-light text-calm-muted">
                          {new Date(doc.fecha_subida).toLocaleDateString('es-ES')}
                        </TableCell>
                        <TableCell className="pr-6">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1.5 rounded-lg text-calm-accent hover:text-calm-text hover:bg-slate-50 transition-colors"
                              onClick={() => handleVerDocumento(doc)}
                              title="Ver documento"
                            >
                              <span className="material-symbols-outlined text-[18px]">visibility</span>
                            </button>
                            <button
                              className="p-1.5 rounded-lg text-calm-accent hover:text-red-500 hover:bg-red-50 transition-colors"
                              onClick={() => handleDeleteDocument(doc.id)}
                              title="Eliminar"
                            >
                              <span className="material-symbols-outlined text-[18px]">delete</span>
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          </div>
        </div>
        )}
        {/* Cambios Masivos Modal */}
        <Dialog open={mostrarCambiosMasivos} onOpenChange={setMostrarCambiosMasivos}>
          <DialogContent className="max-w-lg rounded-huge border-calm-border">
            <DialogHeader>
              <DialogTitle className="font-display font-light text-calm-text text-xl flex items-center gap-2">
                <span className="material-symbols-outlined text-fresh-sky-500">tune</span>
                Cambios Masivos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-calm-muted font-light">
                Aplicar cambios a <span className="font-medium text-calm-text">{selectedDocs.size}</span> documento(s) seleccionado(s).
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Factura</label>
                  <Input type="date" className="rounded-2xl border-calm-border text-sm font-light"
                    value={cambiosMasivosForm.fechaFactura}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, fechaFactura: e.target.value }))}/>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Vencimiento</label>
                  <Input type="date" className="rounded-2xl border-calm-border text-sm font-light"
                    value={cambiosMasivosForm.fechaVencimiento}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, fechaVencimiento: e.target.value }))}/>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha Contable</label>
                  <Input type="date" className="rounded-2xl border-calm-border text-sm font-light"
                    value={cambiosMasivosForm.fechaContable}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, fechaContable: e.target.value }))}/>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Actividad</label>
                  <Select value={cambiosMasivosForm.actividad} onValueChange={v => setCambiosMasivosForm(p => ({ ...p, actividad: v }))}>
                    <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FACTURA">Factura</SelectItem>
                      <SelectItem value="ABONO">Abono</SelectItem>
                      <SelectItem value="RECTIFICATIVA">Rectificativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Serie</label>
                  <Input className="rounded-2xl border-calm-border text-sm font-light" placeholder="A, B, C..."
                    value={cambiosMasivosForm.serie}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, serie: e.target.value }))}/>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Posición Fiscal</label>
                  <Input className="rounded-2xl border-calm-border text-sm font-light" placeholder="Ej: Nacional"
                    value={cambiosMasivosForm.posicionFiscal}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, posicionFiscal: e.target.value }))}/>
                </div>
                <div className="space-y-2 col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Deducibilidad (%)</label>
                  <Input type="number" min="0" max="100" className="rounded-2xl border-calm-border text-sm font-light" placeholder="100"
                    value={cambiosMasivosForm.deducibilidad}
                    onChange={e => setCambiosMasivosForm(p => ({ ...p, deducibilidad: e.target.value }))}/>
                </div>
                <div className="flex items-center gap-3 col-span-2 bg-slate-50 rounded-2xl p-3 border border-calm-border">
                  <Checkbox checked={cambiosMasivosForm.bienesInversion}
                    onCheckedChange={(checked) => setCambiosMasivosForm(p => ({ ...p, bienesInversion: !!checked }))}/>
                  <label className="text-sm text-calm-text font-light">
                    <span className="font-medium">Bienes de Inversión</span>
                    <span className="block text-xs text-calm-muted mt-0.5">
                      Documentos con deducibilidad del IVA
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <button className="ghost-btn" onClick={() => setMostrarCambiosMasivos(false)}>Cancelar</button>
              <button className="bg-gradient-to-r from-fresh-sky-500 to-fresh-sky-600 text-white font-medium rounded-huge px-5 py-2.5 hover:from-fresh-sky-600 hover:to-fresh-sky-700 transition-all text-sm flex items-center gap-2"
                onClick={() => {
                  toast.success(`Cambios masivos aplicados a ${selectedDocs.size} documento(s)`);
                  setMostrarCambiosMasivos(false);
                  setCambiosMasivosForm({ fechaFactura: '', fechaVencimiento: '', fechaContable: '', actividad: '', serie: '', posicionFiscal: '', bienesInversion: false, deducibilidad: '' });
                }}>
                <span className="material-symbols-outlined text-[16px]">check</span>
                Aplicar Cambios
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Upload Modal */}
        <Dialog open={mostrarSubida} onOpenChange={(open) => {
          if (!open && !uploading) {
            setMostrarSubida(false);
            setSelectedFiles([]);
            setUploadClienteId('');
            setUploadTipo('');
            setUploadNotas('');
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-huge border-calm-border">
            <DialogHeader>
              <DialogTitle className="font-display font-light text-calm-text text-xl">Subir Documentos en Nombre del Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubirDocumentos}>
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">
                    Seleccionar Cliente <span className="text-red-400">*</span>
                  </label>
                  <Select value={uploadClienteId} onValueChange={setUploadClienteId} disabled={uploading}>
                    <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                      <SelectValue placeholder="Selecciona un cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allClientes.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nombre_comercial || cliente.razon_social} ({cliente.nif})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-calm-accent font-light">
                    Selecciona para que cliente estas subiendo estos documentos
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">
                    Tipo de Documento <span className="text-red-400">*</span>
                  </label>
                  <Select value={uploadTipo} onValueChange={setUploadTipo} disabled={uploading}>
                    <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                      <SelectValue placeholder="Selecciona un tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emitida_pdf">Facturas emitidas (PDF)</SelectItem>
                      <SelectItem value="recibida_pdf">Factura recibidas (PDF)</SelectItem>
                      <SelectItem value="emitida_excel">Facturas emitidas (Excel)</SelectItem>
                      <SelectItem value="recibida_excel">Facturas recibidas (Excel)</SelectItem>
                      <SelectItem value="otro">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Fecha Contable */}
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Fecha contable</label>
                  <Select value={fechaContable} onValueChange={setFechaContable} disabled={uploading}>
                    <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fecha_factura">Fecha de Factura</SelectItem>
                      <SelectItem value="hoy">Hoy</SelectItem>
                      <SelectItem value="configurable">Fecha Configurable</SelectItem>
                    </SelectContent>
                  </Select>
                  {fechaContable === 'configurable' && (
                    <Input
                      type="date"
                      value={fechaContableCustom}
                      onChange={(e) => setFechaContableCustom(e.target.value)}
                      className="mt-2 rounded-2xl border-calm-border text-sm font-light"
                      disabled={uploading}
                    />
                  )}
                </div>

                {/* Separar Paginas */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-calm-border">
                  <Checkbox
                    id="separarPaginas"
                    checked={separarPaginas}
                    onCheckedChange={(checked) => setSepararPaginas(!!checked)}
                    disabled={uploading}
                  />
                  <label htmlFor="separarPaginas" className="text-sm text-calm-text font-light">
                    <span className="font-medium">Separar paginas</span>
                    <span className="block text-xs text-calm-muted mt-0.5">
                      Cada pagina del PDF se procesara como un documento independiente
                    </span>
                  </label>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">
                    Archivos <span className="text-red-400">*</span>
                  </label>
                  
                  {/* Drag and Drop Zone */}
                  <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                      'relative border-2 border-dashed rounded-huge p-10 text-center transition-all duration-300',
                      isDragging
                        ? 'border-calm-text bg-slate-50 scale-[1.01]'
                        : 'border-calm-border hover:border-calm-accent hover:bg-slate-50/50',
                      uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    )}
                    onClick={() => !uploading && document.getElementById('fileInputAsesor')?.click()}
                  >
                    <div className="mb-4">
                      <span className={cn(
                        'material-symbols-outlined text-[56px]',
                        isDragging
                          ? 'text-calm-text'
                          : 'text-calm-accent'
                      )}>upload_file</span>
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-sm text-calm-text font-light">
                        {isDragging
                          ? 'Suelta los archivos aqui'
                          : <>
                              Arrastra y suelta tus archivos aqui, o{' '}
                              <span className="text-calm-text font-medium">haz clic para seleccionar</span>
                            </>
                        }
                      </p>
                    </div>
                    
                    <p className="text-xs text-calm-accent font-light">
                      Formatos: PDF, Excel (.xls, .xlsx), XML, JPG, PNG
                    </p>
                    <p className="text-xs text-calm-accent/60 font-light mt-1">
                      Maximo 10MB por archivo
                    </p>
                    
                    {/* Hidden File Input */}
                    <input
                      id="fileInputAsesor"
                      type="file"
                      className="hidden"
                      accept=".pdf,.xml,.jpg,.jpeg,.png,.xls,.xlsx"
                      multiple
                      onChange={handleFileInputChange}
                      disabled={uploading}
                    />
                  </div>
                  
                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">
                        Archivos seleccionados ({selectedFiles.length}):
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-slate-50 rounded-2xl p-3 border border-calm-border"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="material-symbols-outlined text-calm-accent text-[20px]">
                                {file.type.includes('pdf') ? 'picture_as_pdf' :
                                 file.type.includes('image') ? 'image' :
                                 file.type.includes('xml') ? 'code' :
                                 file.type.includes('excel') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx') ? 'table_chart' :
                                 'attach_file'}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-light text-calm-text truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-calm-accent font-light">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            {!uploading && (
                              <button
                                type="button"
                                className="ml-3 p-1.5 rounded-xl text-calm-muted hover:text-red-500 hover:bg-red-50 transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFile(index);
                                }}
                                title="Eliminar"
                              >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-calm-muted font-medium">Notas (opcional)</label>
                  <textarea
                    value={uploadNotas}
                    onChange={(e) => setUploadNotas(e.target.value)}
                    className="w-full border border-calm-border rounded-2xl p-4 text-sm font-light text-calm-text placeholder:text-calm-accent focus:outline-none focus:ring-1 focus:ring-calm-text/20 focus:border-calm-text/30 transition-all"
                    rows={3}
                    placeholder="Anade cualquier nota o aclaracion sobre estos documentos..."
                    disabled={uploading}
                  ></textarea>
                </div>

                {uploading && (
                  <div className="bg-slate-50 border border-calm-border rounded-2xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-calm-text"></div>
                      <span className="text-sm font-light text-calm-text">
                        Subiendo documentos... {uploadProgress}%
                      </span>
                    </div>
                    <Progress value={uploadProgress} className="h-1.5" />
                  </div>
                )}

                {!uploading && (
                  <div className="bg-slate-50 border border-calm-border rounded-2xl p-4">
                    <div className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-calm-accent text-[18px] mt-0.5">info</span>
                      <p className="text-sm text-calm-muted font-light">
                        <span className="font-medium text-calm-text">Subida masiva:</span> Puedes seleccionar multiples archivos a la vez. 
                        Todos se subiran con el mismo tipo de documento y para el mismo cliente. 
                        Si necesitas subir diferentes tipos o para diferentes clientes, hazlo en lotes separados.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 ghost-btn justify-center border-calm-text text-calm-text hover:bg-calm-text hover:text-white"
                    disabled={uploading}
                  >
                    <span className="material-symbols-outlined text-[18px]">upload</span>
                    {uploading ? 'Subiendo...' : 'Subir Documento(s)'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 ghost-btn justify-center"
                    disabled={uploading}
                    onClick={() => {
                      setMostrarSubida(false);
                      setSelectedFiles([]);
                      setUploadClienteId('');
                      setUploadTipo('');
                      setUploadNotas('');
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ═══ Modal: Cambiar Empresa ═══ */}
        <Dialog open={mostrarCambiarEmpresa} onOpenChange={setMostrarCambiarEmpresa}>
          <DialogContent className="sm:max-w-md rounded-3xl border-calm-border">
            <DialogHeader>
              <DialogTitle className="font-display font-light text-calm-text flex items-center gap-2">
                <span className="material-symbols-outlined text-steel-azure-500">business</span>
                Cambiar Empresa
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm font-light text-calm-muted">
                Mover {selectedDocs.size} documento(s) seleccionado(s) a otra empresa:
              </p>
              <Select value={empresaDestinoId} onValueChange={setEmpresaDestinoId}>
                <SelectTrigger className="rounded-2xl border-calm-border text-sm font-light">
                  <SelectValue placeholder="Selecciona empresa destino..." />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-calm-border">
                  {allClientes.map(c => (
                    <SelectItem key={c.id} value={c.id} className="rounded-xl">
                      {c.nombre_comercial || c.razon_social} ({c.nif})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-2xl" onClick={() => { setMostrarCambiarEmpresa(false); setEmpresaDestinoId(''); }}>
                Cancelar
              </Button>
              <Button className="rounded-2xl bg-steel-azure-500 hover:bg-steel-azure-600 text-white" onClick={handleCambiarEmpresaBulk} disabled={procesandoAccion || !empresaDestinoId}>
                {procesandoAccion ? 'Cambiando...' : 'Cambiar Empresa'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══ Modal: Asignar Tercero ═══ */}
        <Dialog open={mostrarAsignarTercero} onOpenChange={setMostrarAsignarTercero}>
          <DialogContent className="sm:max-w-md rounded-3xl border-calm-border">
            <DialogHeader>
              <DialogTitle className="font-display font-light text-calm-text flex items-center gap-2">
                <span className="material-symbols-outlined text-steel-azure-500">person_add</span>
                Asignar Tercero
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm font-light text-calm-muted">
                Asignar emisor/tercero a {selectedDocs.size} documento(s) seleccionado(s):
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-light text-calm-muted">NIF / CIF *</Label>
                  <Input
                    value={terceroNif}
                    onChange={(e) => setTerceroNif(e.target.value.toUpperCase())}
                    placeholder="B12345678"
                    className="rounded-2xl border-calm-border text-sm font-light font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-light text-calm-muted">Razón Social</Label>
                  <Input
                    value={terceroNombre}
                    onChange={(e) => setTerceroNombre(e.target.value)}
                    placeholder="Nombre del tercero"
                    className="rounded-2xl border-calm-border text-sm font-light"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-2xl" onClick={() => { setMostrarAsignarTercero(false); setTerceroNif(''); setTerceroNombre(''); }}>
                Cancelar
              </Button>
              <Button className="rounded-2xl bg-steel-azure-500 hover:bg-steel-azure-600 text-white" onClick={handleAsignarTerceroBulk} disabled={procesandoAccion || !terceroNif.trim()}>
                {procesandoAccion ? 'Asignando...' : 'Asignar Tercero'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    </>
  );
}
